<script lang="ts">
  import { onMount, onDestroy, tick } from "svelte";
  import { TabItem, Tabs, Toggle, Button, Input, Spinner } from "flowbite-svelte";
  import { createGrid } from "ag-grid-community";
  import type { GridApi } from "ag-grid-community";
  import "ag-grid-enterprise";
  import "ag-grid-community/styles/ag-grid.css";
  import "ag-grid-community/styles/ag-theme-quartz.css";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faTable, faChevronDown, faCircleXmark, faFileExcel, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
  import { theme } from "../../services/stores/theme-store.js";
  import { projectStructureStore, studyExtractStore } from "./table-editor/stores.js";
  import { loadTargetExtract, saveTargetExtract } from "./data/target-data-handler.js";
  import { getProject } from "../../services/api/data-handler.js";
  import type { Project } from "../../services/api/types.js";
  import { createCellWithIndicator, DEFAULT_CURRENT_DOCUMENT_ID } from "./indicator-utils.js";
  import { getDisplayName, getCustomValue } from "./table-editor/grouper-utils.js";
  import { updateTargetCell, updateReferenceCell } from "./table-editor/target-table-processing.js";
  import { userStore } from "../../services/stores/user-store.js";
  import { getLookupCellRenderer } from "./table-editor/cell-editor-config.js";
  // @ts-ignore - runtime import of TS helper via .js extension
  import { deriveDatasetKey, getProjectStructureUrl, getStudyExtractUrls } from "./extract-helper.js";
  import { logInfo, logError, logWarning } from "../../services/logging/log-handler.js";

  // ClickOutside action for dropdown
  function clickOutside(node: HTMLElement, callback: () => void) {
    function handleClick(event: MouseEvent) {
      const target = event.target as Node;
      // Check if click is outside the dropdown wrapper (which includes both button and dropdown)
      if (dropdownWrapper && !dropdownWrapper.contains(target)) {
        callback();
      }
    }
    document.addEventListener('click', handleClick, true);
    return {
      destroy() {
        document.removeEventListener('click', handleClick, true);
      }
    };
  }
  
  type SectionType = "reference" | "topic";
  type MainSection = {
    id: string;
    label: string;
    type: SectionType;
    sourceId: string;
  };

  type StudyExtractEntry = {
    id: string;
    name: string;
    extract: any;
  };

  const STUDY_COLUMN_FIELD = "__studyName";
  const STUDY_ID_FIELD = "__studyId";
  const STUDY_ROW_KEY_FIELD = "__rowKey";
  const STUDY_SOURCE_FIELD = "__studySourceInfo";
  const DEFAULT_DOCUMENT_ID = DEFAULT_CURRENT_DOCUMENT_ID;
  
  let storesInitialized = false;
  let storeInitializationPromise: Promise<void> | null = null;
  let currentDatasetKey: string | null = null;
  let studyExtractEntries: StudyExtractEntry[] = [];
  
  async function fetchJson(url: string): Promise<any> {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`Failed to load JSON from ${url} (${response.status} ${response.statusText})`);
    }
    return response.json();
  }
  
    function toTitleCase(value: string): string {
      if (!value) return "";
      return value
        .replace(/[_-]+/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
  
    function processFields(fields: any[] | undefined): void {
      if (!Array.isArray(fields)) {
        return;
      }
      fields.forEach((field: any) => {
        const isReferenceField = field?.refTable || field?.refID;
        if (!field.type && !isReferenceField) {
          field.type = "string";
        }
        if (Array.isArray(field.fields)) {
          processFields(field.fields);
        }
      });
    }
  
    function getReferenceColumnLabel(references: any[], refId: string, columnId: string): string {
      if (!refId) return columnId || "";
      const reference = references?.find((ref: any) => ref.id === refId);
      if (!reference) {
        return columnId || refId;
      }
      const collections = reference.columns || reference.fields || [];
      const target = collections.find((item: any) => item.id === columnId);
      if (target?.label) {
        return target.label;
      }
      return columnId || refId;
    }
  
    function normalizeProjectStructure(structure: any): any {
      if (!structure) return structure;
      const normalized = JSON.parse(JSON.stringify(structure));
  
      normalized.references?.forEach((ref: any) => {
        if (Array.isArray(ref.fields)) {
          processFields(ref.fields);
        }
        if (Array.isArray(ref.columns)) {
          processFields(ref.columns);
        }
      });
  
      const referenceLookup = normalized.references || [];
  
      normalized.topics?.forEach((topic: any) => {
        if (topic?.grouper?.levels) {
          topic.grouper.levels = topic.grouper.levels.map((level: any, index: number) => {
            const levelNumber = String(level.level ?? level.number ?? index + 1);
            const refDefs = (level.refs || []).map((ref: any) => {
              const refTable = ref.refId;
              const columnId = ref.columnId;
              return {
                refTable,
                columnId,
                label: getReferenceColumnLabel(referenceLookup, refTable, columnId),
              };
            });
  
            const sources: any[] = refDefs.map((refDef: any) => ({
              type: "reference",
              refTable: refDef.refTable,
              refType: refDef.label || refDef.refTable,
            }));
  
            return {
              ...level,
              number: levelNumber,
              refDefs,
              sources,
            };
          });
        }
  
        if (Array.isArray(topic.tables)) {
          topic.tables = topic.tables.map((table: any) => {
            const columns = Array.isArray(table.columns) ? [...table.columns] : [];
            processFields(columns);
            return { ...table, columns };
          });
        }
      });
  
      return normalized;
    }
  
    function getNumericOrderValue(row: any, fallbackIndex: number): number {
      if (typeof row?.order === "number" && !Number.isNaN(row.order)) {
        return row.order;
      }
      const parsed = parseInt(row?.order ?? "", 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
      return fallbackIndex;
    }
  
    function normalizeTopicGrouper(rawGrouper: any, structureTopic: any) {
      const topicId = structureTopic?.id || "unknown";
      if (!Array.isArray(rawGrouper)) {
        throw new Error(`[grouper] Topic "${topicId}" grouper must be an array`);
      }
      const levelDefinitions = Array.isArray(structureTopic?.grouper?.levels)
        ? structureTopic.grouper.levels
        : [];
  
      return rawGrouper.map((row: any, index: number) => {
        if (!row || typeof row !== "object") {
          throw new Error(`[grouper] Topic "${topicId}" contains an invalid row at index ${index}`);
        }
  
        if (!Array.isArray(row.levels)) {
          throw new Error(`[grouper] Topic "${topicId}" row ${index + 1} missing levels array`);
        }
  
        const rowId = row.grouperId;
        if (typeof rowId !== "string" || !rowId.trim()) {
          throw new Error(`[grouper] Topic "${topicId}" row ${index + 1} missing grouperId`);
        }
  
        const normalizedLevels = levelDefinitions.map((levelDef: any, levelIndex: number) => {
          const levelNumber = String(levelDef.level ?? levelDef.number ?? levelIndex + 1);
          const rawLevel = row.levels.find(
            (lvl: any) => String(lvl.level ?? lvl.number ?? "") === levelNumber
          );
  
          if (!rawLevel || !Array.isArray(rawLevel.refs)) {
            throw new Error(
              `[grouper] Topic "${topicId}" row "${rowId}" level "${levelNumber}" missing refs array`
            );
          }
  
          const expectedRefs = Array.isArray(levelDef.refDefs) ? levelDef.refDefs.length : 0;
          if (rawLevel.refs.length !== expectedRefs) {
            throw new Error(
              `[grouper] Topic "${topicId}" row "${rowId}" level "${levelNumber}" expected ${expectedRefs} refs but received ${rawLevel.refs.length}`
            );
          }
  
          return {
            level: levelNumber,
            refs: rawLevel.refs.map((ref: any) => (typeof ref === "string" ? ref : "")),
          };
        });
  
        const customLevel = row.levels.find(
          (lvl: any) => String(lvl.level ?? lvl.number ?? "").toLowerCase() === "custom"
        );
        let customValue = "";
        if (typeof row.custom === "string" && row.custom.trim()) {
          customValue = row.custom.trim();
        } else if (typeof customLevel?.value === "string" && customLevel.value.trim()) {
          customValue = customLevel.value.trim();
        }
  
        if (customValue) {
          normalizedLevels.push({ level: "custom", value: customValue });
        }
  
        // Preserve the cells array if it exists
        const cells = Array.isArray(row.cells) ? row.cells : [];
  
        return {
          grouperId: String(rowId),
          order: getNumericOrderValue(row, index + 1),
          levels: normalizedLevels,
          cells: cells
        };
      });
    }
  
    function normalizeStudyExtract(study: any, projectStructure: any): any {
      if (!study) return study;
      const normalized = JSON.parse(JSON.stringify(study));
  
      const topicStructureMap = new Map<string, any>();
      projectStructure?.topics?.forEach((topic: any) => {
        topicStructureMap.set(topic.id, topic);
      });
  
      normalized.topics = (normalized.topics || []).map((topic: any) => {
        const structureTopic = topicStructureMap.get(topic.id);
        const normalizedTopic: any = { ...topic };
  
        const rawGrouperData = topic.grouper ?? [];
        normalizedTopic.grouper = normalizeTopicGrouper(rawGrouperData, structureTopic);
  
        normalizedTopic.tables = (topic.tables || []).map((table: any) => {
          const normalizedRows = (table.rows || []).map((row: any) => {
            const { grouperId, ...rest } = row;
            return {
              ...rest,
              id: grouperId || row.id,
            };
          });
          return {
            ...table,
            rows: normalizedRows,
          };
        });
  
        return normalizedTopic;
      });
  
      return normalized;
    }
  
  function deriveStudyMetadata(study: any, index: number, projectStructure: any): { id: string; name: string } {
    const fallbackName = `Study ${index + 1}`;
    const studyReference = study?.references?.find((ref: any) => ref.id === "study");
    if (!studyReference) {
      return { id: `study_${index + 1}`, name: fallbackName };
    }
    
    // Get the reference structure to find the nameRef field
    const refStructure = projectStructure?.references?.find((ref: any) => ref.id === "study");
    const nameRef = refStructure?.nameRef || "studyid"; // Default to "studyid" if nameRef not specified
    
    let studyNameValue: string | undefined;
    
    // Handle table format (rows with cells)
    if (Array.isArray(studyReference.rows) && studyReference.rows.length > 0) {
      const firstRow = studyReference.rows[0];
      if (firstRow?.cells) {
        const nameCell = firstRow.cells.find((c: any) => c.id === nameRef);
        studyNameValue = nameCell?.value;
      }
    }
    // Handle object format (values array) for backward compatibility
    else if (Array.isArray(studyReference.values)) {
      const studyIdValue = studyReference.values.find?.((value: any) => value.id === nameRef);
      studyNameValue = studyIdValue?.value;
    }
    
    const normalizedId = typeof studyNameValue === "string" && studyNameValue.trim() ? studyNameValue.trim() : `study_${index + 1}`;
    const normalizedName = typeof studyNameValue === "string" && studyNameValue.trim() ? studyNameValue.trim() : fallbackName;

    return { id: normalizedId, name: normalizedName };
  }

  function initializeStores(datasetKey: string, projectStructureData: any, studyExtractDataList: any[]) {
    const normalizedStructure = normalizeProjectStructure(projectStructureData);
    projectStructureStore.set(normalizedStructure);
  
    const studies: StudyExtractEntry[] = studyExtractDataList.map((studyExtractData: any, index: number) => {
      const storageKey = `study_demo_${datasetKey}_${index + 1}`;
      const processedStudy = loadTargetExtract("study", storageKey, studyExtractData);
      const normalizedStudy = normalizeStudyExtract(processedStudy, normalizedStructure);
      const metadata = deriveStudyMetadata(normalizedStudy, index, normalizedStructure);
      return {
        id: metadata.id,
        name: metadata.name,
        extract: normalizedStudy,
      };
    });

    studyExtractEntries = studies;

    const primaryStudy = studies[0]?.extract ?? { topics: [], references: [] };
    studyExtractStore.set(primaryStudy);
  
    storesInitialized = true;
    currentDatasetKey = datasetKey;
  }
  
  function ensureStoresLoaded(): Promise<void> {
    const datasetKey = deriveDatasetKey(projectId);
  
    if (storesInitialized && currentDatasetKey === datasetKey) {
      return Promise.resolve();
    }
  
    if (
      storeInitializationPromise &&
      currentDatasetKey === datasetKey
    ) {
      return storeInitializationPromise;
    }
  
    storesInitialized = false;
    currentDatasetKey = datasetKey;
    projectStructureStore.set({});
    studyExtractStore.set({ topics: [], references: [] });
    studyExtractEntries = [];
    hasLoadedInitialTab = false;
    activeTab = "";
  
    const loadPromise = (async () => {
      const projectStructureUrl = getProjectStructureUrl(datasetKey);
      const studyExtractUrls = getStudyExtractUrls(datasetKey);

      const studyFetches: Promise<any>[] = [];
      for (const url of studyExtractUrls) {
        studyFetches.push(fetchJson(url));
      }
      const fetchPromises: Promise<any>[] = [
        fetchJson(projectStructureUrl),
        ...studyFetches
      ];

      const [projectStructureData, ...studyExtractDataList] = await Promise.all(fetchPromises);

      initializeStores(datasetKey, projectStructureData, studyExtractDataList);
    })();
  
    storeInitializationPromise = loadPromise;
  
    loadPromise
      .then(() => {
        storeInitializationPromise = null;
      })
      .catch((error) => {
        storesInitialized = false;
        currentDatasetKey = null;
        storeInitializationPromise = null;
        logError(error, "ProjectExtract", undefined, { message: "Failed to load grid sheet data" });
        throw error;
      });
  
    return loadPromise;
  }
  function ensureProjectDetailsLoaded(): Promise<void> {
    if (!projectId) {
      projectDetails = null;
      currentProjectDetailsId = null;
      return Promise.resolve();
    }

    if (currentProjectDetailsId === projectId && projectDetails) {
      return Promise.resolve();
    }

    if (projectLoadPromise && currentProjectDetailsId === projectId) {
      return projectLoadPromise;
    }

    currentProjectDetailsId = projectId;
    projectDetails = null;

    const loadPromise = (async () => {
      try {
        const result = await getProject(projectId);
        projectDetails = result?.project ?? null;
      } catch (error) {
        projectDetails = null;
        logError(error, "ProjectExtract", undefined, { message: "ProjectExtract failed to load project details" });
      } finally {
        projectLoadPromise = null;
      }
    })();

    projectLoadPromise = loadPromise;

    return loadPromise;
  }
  export let show: boolean = false;
  export let projectId: string = '';
  export let showExternalLink: boolean = false;
  let projectTitle = "";
  let projectDetails: Project | null = null;
  let projectLoadPromise: Promise<void> | null = null;
  let currentProjectDetailsId: string | null = null;
  let showIndicators = true;
  let quickFilterText = "";
  
  // Study filter state
  let selectedStudies = new Set<string>();
  let studyFilterText = "";
  let isDropdownOpen = false;
  let selectAllCheckbox: HTMLInputElement | null = null;
  let dropdownWrapper: HTMLDivElement | null = null;
  let dropdownContent: HTMLDivElement | null = null;
  let hasInitializedStudies = false;
  let lastStudyExtractEntriesLength = 0;
  
  // Store references
    $: projectStructure = $projectStructureStore || {};
    $: studyExtract = $studyExtractStore || { topics: [], references: [] };
    // also, so now I understand why you moved through each tab, because we only have 1 grid, and so the tab triggers loading with that data, then export and move on. perhaps we can have a hidden grid, and then have a loop in the export that basically moves through each item (not tab) then loads and exports the grid.

    // so am wondering if we can have a popup with just a spinner, and perhaps a hidden grid, where it loops through each one, loads and export. can close when done automatically.  
    // Grid refs
    let gridDiv: HTMLDivElement;
    let gridApi: GridApi | null = null;
    let gridInstance: any = null;
    
    // Hidden grid for export (to avoid tab switching)
    let hiddenGridDiv: HTMLDivElement;
    let hiddenGridApi: GridApi | null = null;
    let hiddenGridInstance: any = null;
    
    // Export progress state
    let isExporting = false;
    let exportProgress = '';
    let exportModalOpen = false;
    
    // Store grid APIs for all tabs to enable multi-sheet export
    let gridApisByTab = new Map<string, GridApi>();
  
    // Tab state
    let activeTab: string = "";
    let mainSections: MainSection[] = [];
  
  // Initialize selected studies to all studies when studies are first loaded
  // Only initialize when studies are first loaded, not when user unchecks all
  $: if (studyExtractEntries.length > 0) {
    // Check if studies list has changed (new studies loaded)
    const studiesLengthChanged = studyExtractEntries.length !== lastStudyExtractEntriesLength;
    
    if (studiesLengthChanged) {
      // Studies have changed - update the length tracker
      lastStudyExtractEntriesLength = studyExtractEntries.length;
      
      // Only initialize if we haven't initialized yet OR if all selected studies are no longer in the list
      if (!hasInitializedStudies) {
        // First time: initialize to all studies
        selectedStudies = new Set(studyExtractEntries.map(entry => entry.id));
        hasInitializedStudies = true;
      } else {
        // Studies changed: update selectedStudies to only include studies that still exist
        const newStudyIds = new Set(studyExtractEntries.map(entry => entry.id));
        selectedStudies = new Set([...selectedStudies].filter(id => newStudyIds.has(id)));
        
        // If no studies remain selected after filtering, select all new studies
        if (selectedStudies.size === 0) {
          selectedStudies = new Set(studyExtractEntries.map(entry => entry.id));
        }
      }
    }
  } else if (studyExtractEntries.length === 0) {
    // Reset when studies are cleared
    hasInitializedStudies = false;
    lastStudyExtractEntriesLength = 0;
    selectedStudies = new Set<string>();
  }
  
  // Filter studies based on search text
  $: filteredStudies = studyExtractEntries.filter(entry => 
    entry.name.toLowerCase().includes(studyFilterText.toLowerCase())
  );
  
  // Compute Select All checkbox state
  $: allStudiesSelected = studyExtractEntries.length > 0 && selectedStudies.size === studyExtractEntries.length;
  $: someStudiesSelected = selectedStudies.size > 0 && selectedStudies.size < studyExtractEntries.length;
  $: selectAllIndeterminate = someStudiesSelected;
  
  // Set indeterminate state on checkbox (must be done via property, not attribute)
  $: if (selectAllCheckbox) {
    selectAllCheckbox.indeterminate = selectAllIndeterminate;
  }
  
  // Reactive statement to apply quick filter when it changes (persistent across tabs)
  $: if (gridApi && quickFilterText !== undefined) {
    gridApi.setGridOption("quickFilterText", quickFilterText || '');
  }
  
  // Reactive statement to refresh cells when indicator toggle changes
  $: if (gridApi && showIndicators !== undefined) {
    gridApi.refreshCells({ force: true });
  }
  
  // Toggle study selection
  function toggleStudy(studyId: string) {
    const newSelectedStudies = new Set(selectedStudies);
    if (newSelectedStudies.has(studyId)) {
      newSelectedStudies.delete(studyId);
    } else {
      newSelectedStudies.add(studyId);
    }
    selectedStudies = newSelectedStudies; // Trigger reactivity by creating new Set
    // Apply filter
    if (gridApi) {
      gridApi.onFilterChanged();
    }
  }
  
  // Toggle all studies
  function toggleAllStudies() {
    if (allStudiesSelected) {
      // All are selected, so unselect all
      selectedStudies = new Set();
    } else {
      // Some or none are selected, so select all
      selectedStudies = new Set(studyExtractEntries.map(entry => entry.id));
    }
    // Apply filter
    if (gridApi) {
      gridApi.onFilterChanged();
    }
  }
  
  // External filter functions for AG Grid
  function isExternalFilterPresent(): boolean {
    return selectedStudies.size < studyExtractEntries.length;
  }
  
  function doesExternalFilterPass(node: any): boolean {
    if (selectedStudies.size === 0) {
      return false; // No studies selected, hide all
    }
    if (selectedStudies.size === studyExtractEntries.length) {
      return true; // All studies selected, show all
    }
    const studyId = node.data?.[STUDY_ID_FIELD];
    return studyId && selectedStudies.has(studyId);
  }
    let hasLoadedInitialTab = false;
  
    // Data state
    let currentData: any = null;
    let currentStructure: any = null;
  
    function buildMainSections(structure: any): MainSection[] {
      const sections: MainSection[] = [];
  
      if (Array.isArray(structure?.references)) {
        structure.references.forEach((ref: any) => {
          const label = ref.label || toTitleCase(ref.id);
          sections.push({
            id: `reference:${ref.id}`,
            label,
            type: "reference",
            sourceId: ref.id,
          });
        });
      }
  
      if (Array.isArray(structure?.topics)) {
        structure.topics.forEach((topic: any) => {
          const label = topic.label || toTitleCase(topic.id);
          sections.push({
            id: `topic:${topic.id}`,
            label,
            type: "topic",
            sourceId: topic.id,
          });
        });
      }
  
      return sections;
    }
  
    $: mainSections = buildMainSections(projectStructure);
  
    // Helper function to get reference value by collection and ID
  function getReferenceValue(collectionId: string, itemId: string, studyContext: any = studyExtract): string {
      const targetStudy = studyContext || {};
      const collection = targetStudy?.references?.find((ref: any) => ref.id === collectionId);
      if (!collection) return itemId;
      
      // Get the reference structure to find the nameRef field
      const refStructure = projectStructure?.references?.find((ref: any) => ref.id === collectionId);
      const nameRef = refStructure?.nameRef;
      
      // Handle both 'rows' structure (for tables) and 'values' structure (for objects)
      if (collection.rows) {
        const row = collection.rows.find((r: any) => r.id === itemId);
        if (row?.cells) {
          // Use nameRef if specified, otherwise fall back to 'name' or 'title'
          let nameCell;
          if (nameRef) {
            // Use the nameRef field specified in the reference structure
            nameCell = row.cells.find((c: any) => c.id === nameRef);
          } else {
            // Fallback to 'name' or 'title' for backward compatibility
            nameCell = row.cells.find((c: any) => c.id === 'name' || c.id === 'title');
          }
          return nameCell?.value || itemId;
        }
      }
      
      if (collection.values) {
        const value = collection.values.find((v: any) => v.id === itemId);
        return value?.value || itemId;
      }
      
      return itemId;
    }
  
    function ensureGrouperArrayLocal(grouper: any, context: string): any[] {
      if (!Array.isArray(grouper)) {
        throw new Error(`[grouper] Expected grouper array for ${context}`);
      }
      return grouper;
    }
  
    function findGrouperRowLocal(grouper: any, rowId: string, fallbackIndex?: number): any {
      const rows = ensureGrouperArrayLocal(grouper, `finding row "${rowId}"`);
      let match = rows.find((row: any) => String(row?.grouperId ?? '') === String(rowId));
  
      if (!match && fallbackIndex !== undefined && fallbackIndex >= 0 && fallbackIndex < rows.length) {
        match = rows[fallbackIndex];
      }
  
      if (!match) {
        throw new Error(`[grouper] Row "${rowId}" not found`);
      }
  
      return match;
    }
  
    function getCustomValueLocal(row: any): string {
      if (!row) return '';
      if (Array.isArray(row.levels)) {
        const customLevel = row.levels.find((lvl: any) => {
          const identifier = String(lvl.level ?? lvl.number ?? '').toLowerCase();
          return identifier === 'custom';
        });
        if (typeof customLevel?.value === 'string' && customLevel.value.trim()) {
          return customLevel.value.trim();
        }
      }
      if (typeof row.custom === 'string' && row.custom.trim()) {
        return row.custom.trim();
      }
      return '';
    }
  
    function getLevelRefsLocal(row: any, levelNumber: string | number): string[] {
      if (!Array.isArray(row?.levels)) {
        return [];
      }
  
      const target = row.levels.find(
        (lvl: any) => String(lvl.level ?? lvl.number ?? '') === String(levelNumber)
      );
  
      if (!target || !Array.isArray(target.refs)) {
        return [];
      }
  
      return target.refs.map((ref: any) => (typeof ref === 'string' ? ref : ''));
    }
  
    function getLevelDisplayNameLocal(row: any, levelStructure: any, studyContext?: any): string {
      if (!levelStructure) return '';
  
      const levelNumber = String(levelStructure?.level ?? levelStructure?.number ?? '');
      const refs = getLevelRefsLocal(row, levelNumber);
      const labels: string[] = [];
  
      (levelStructure?.sources || [])
        .filter((source: any) => source?.type === 'reference')
        .forEach((source: any, index: number) => {
          const refId = refs[index];
          if (!refId) return;
          labels.push(getReferenceValue(source.refTable, refId, studyContext));
        });
  
      if (labels.length === 0 && levelNumber.toLowerCase() === 'custom') {
        const customValue = getCustomValueLocal(row);
        if (customValue) {
          labels.push(customValue);
        }
      }
  
      return labels.join(' ● ');
    }

    function getGroupNameFromGrouperRow(grouperRow: any, studyContext: any, grouperStructure?: any): string {
      if (!grouperRow) return '';
      
      // Use the shared getDisplayName function from grouper-utils.ts
      // This handles study fallback and custom values properly
      // It returns the custom value if it exists, or reference values, or study name as fallback
      if (studyContext && grouperStructure) {
        try {
          const displayName = getDisplayName(grouperRow, studyContext, { grouperStructure });
          if (displayName && displayName !== '-') {
            return displayName;
          }
        } catch (error) {
          logError(error, "ProjectExtract", undefined, { message: `Error getting display name from getDisplayName` });
        }
      }
      
      // Fallback: Use the grouper structure's first level to get the display name
      // This is the correct way for normalized grouper rows that have levels with refs
      if (grouperStructure?.levels && grouperStructure.levels.length > 0) {
        const firstLevelStructure = grouperStructure.levels[0];
        const displayName = getLevelDisplayNameLocal(grouperRow, firstLevelStructure, studyContext);
        if (displayName && displayName !== '-') {
          return displayName;
        }
      }
      
      // Check for custom value (e.g., "Custom 1") - this should be handled by getDisplayName
      // but if it wasn't, check here as well
      const customValue = getCustomValue(grouperRow);
      if (customValue) {
        return customValue;
      }
      
      // Fallback: try to get from levels array directly (for non-normalized data)
      if (Array.isArray(grouperRow.levels) && grouperRow.levels.length > 0) {
        const firstLevel = grouperRow.levels[0];
        // Check if this is normalized data (has refs)
        if (Array.isArray(firstLevel.refs) && firstLevel.refs.length > 0) {
          // Use the grouper structure to resolve refs
          if (grouperStructure?.levels && grouperStructure.levels.length > 0) {
            const firstLevelStructure = grouperStructure.levels[0];
            const displayName = getLevelDisplayNameLocal(grouperRow, firstLevelStructure, studyContext);
            if (displayName && displayName !== '-') {
              return displayName;
            }
          }
        }
        // Check if this is non-normalized data (has cells)
        const levelCells = firstLevel?.cells || [];
        const nameCell = levelCells.find((c: any) => c.id === 'name');
        if (nameCell) {
          if (nameCell.ref && nameCell.refID) {
            return getReferenceValue(nameCell.ref, nameCell.refID, studyContext);
          } else if (nameCell.value) {
            return nameCell.value;
          }
        }
      }
      
      // Try to get group name from direct cells structure (legacy format)
      if (Array.isArray(grouperRow.cells)) {
        const directNameCell = grouperRow.cells.find((c: any) => c.id === 'name');
        if (directNameCell) {
          if (directNameCell.ref && directNameCell.refID) {
            return getReferenceValue(directNameCell.ref, directNameCell.refID, studyContext);
          } else if (directNameCell.value) {
            return directNameCell.value;
          }
        }
      }
      
      // Final fallback: return empty string so caller can handle it
      // (The caller will convert it to "Study" if needed)
      return '';
    }
  
    // Helper to resolve column reference
    function resolveColumnReference(
      column: any,
      rowId: string,
      rowIndex: number,
      grouper: any,
      tables: any[],
      grouperStructure?: any
    ): string {
      if (!column.refTable || !column.refID) return '';
  
      if (column.refTable === 'grouper') {
        if (!grouperStructure) {
          throw new Error('[grouper] Grouper structure required to resolve column reference');
        }
  
        const grouperRow = findGrouperRowLocal(grouper, rowId, rowIndex);
  
        if (column.refID === 'name') {
          if (!$studyExtractStore) {
            throw new Error('[grouper] Study extract store not initialised');
          }
  
          // Use getDisplayName from grouper-utils.ts which handles "Study" fallback correctly
          const displayName = getDisplayName(grouperRow, $studyExtractStore, { grouperStructure });
          
          // If the display name is empty or matches rowId, show "Study" instead
          const rowId = grouperRow?.grouperId || grouperRow?.id;
          const rowIdStr = rowId ? String(rowId) : '';
          
          // Check if displayName is empty, rowId, or study name - if so, show "Study"
          if (!displayName || displayName === '-' || displayName === rowIdStr) {
            const customValue = getCustomValue(grouperRow);
            if (customValue) {
              return customValue;
            }
            return 'Study';
          }
          
          return displayName;
        }
  
        if (column.refID === 'custom' || column.refID === 'Custom') {
          return getCustomValueLocal(grouperRow);
        }
  
        return '';
      }
  
      const table = tables.find((t: any) => t.id === column.refTable);
      if (!table?.rows) return '';
  
      const row = table.rows.find((r: any) => String(r.grouperId ?? r.id ?? '') === String(rowId));
      if (!row?.cells) return '';
  
      const cell = row.cells.find((c: any) => c.id === column.refID);
      return cell?.value || '';
    }
  
    function createStudyColumnDef(label: string = "Study") {
      return {
        headerName: label,
        field: STUDY_COLUMN_FIELD,
        colId: STUDY_COLUMN_FIELD,
        pinned: "left",
        width: 220,
        minWidth: 180,
        maxWidth: 280,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        resizable: false,
        sortable: false,
        filter: false,
        lockPosition: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        cellStyle: {
          "white-space": "normal",
          "word-break": "break-word",
          "padding": "8px 8px",
        },
        cellRenderer: (params: any) => {
          const value = params.value || "-";
          const sourceInfo = params.data?.[STUDY_SOURCE_FIELD];
          return showIndicators
            ? createCellWithIndicator(value, sourceInfo, DEFAULT_DOCUMENT_ID)
            : value;
        },
      };
    }

    // Create column definitions for reference tables
    function createReferenceColumnDefs(columns: any[]): any[] {
      const studyColumn = createStudyColumnDef("Study");
      const referenceColumns: any[] = [];
      
      columns.forEach((column, index) => {
        const isFirstColumn = index === 0;
        const isFixed = isFirstColumn;
        const isMultiline = column.multiline === true;
        
        // Handle grouped columns (e.g., study_design with fields: blinding, stratification_factors)
        if (column.type === 'group' && Array.isArray(column.fields)) {
          const groupColumns = column.fields.map((field: any) => {
            const fieldMultiline = field.multiline === true || field.type === 'multiline';
            return {
              headerName: field.label || field.id,
              field: field.id,
              colId: field.id,
              pinned: undefined,
              width: fieldMultiline ? 200 : undefined,
              minWidth: fieldMultiline ? 200 : undefined,
              maxWidth: fieldMultiline ? 200 : undefined,
              suppressSizeToFit: fieldMultiline,
              suppressAutoSize: fieldMultiline,
              resizable: false,
              sortable: false,
              filter: false,
              editable: true, // Enable editing for grouped column fields
              lockPosition: false,
              suppressHeaderMenuButton: true,
              suppressHeaderContextMenu: true,
              wrapText: true, // Enable text wrapping (row height calculated by getRowHeight)
              cellStyle: {
                'white-space': 'normal',
                'word-wrap': 'break-word',
                'overflow-wrap': 'break-word',
                'padding': '8px 8px',
                'line-height': 'normal',
                'vertical-align': 'top'
              },
              cellRenderer: (params: any) => {
                const value = params.value || "-";
                const sourceInfo = params.data?.[`${params.colDef.field}_sourceInfo`];
                return showIndicators
                  ? createCellWithIndicator(value, sourceInfo, DEFAULT_DOCUMENT_ID)
                  : value;
              }
            };
          });

          if (groupColumns.length > 0) {
            referenceColumns.push({
              headerName: column.label || column.id,
              marryChildren: true,
              children: groupColumns,
              resizable: false,
              lockPosition: false,
              suppressHeaderMenuButton: true,
              suppressHeaderContextMenu: true,
            });
          }
        } else {
          // Handle regular columns
          referenceColumns.push({
            headerName: column.label || column.id,
            field: column.id,
            colId: column.id,
            pinned: isFixed ? 'left' : undefined,
            width: isMultiline ? 200 : (isFixed ? 200 : undefined),
            minWidth: isMultiline ? 200 : (isFixed ? 200 : undefined),
            maxWidth: isMultiline ? 200 : (isFixed ? 200 : undefined),
            suppressSizeToFit: isMultiline || isFixed,
            suppressAutoSize: isMultiline || isFixed,
            resizable: false,
            sortable: false,
            filter: false,
            editable: true, // Enable editing for reference columns
            lockPosition: isFixed,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            wrapText: true, // Enable text wrapping for all reference columns (row height calculated by getRowHeight)
            cellStyle: {
              'white-space': 'normal',
              'word-wrap': 'break-word',
              'overflow-wrap': 'break-word',
              'padding': '8px 8px',
              'line-height': 'normal',
              'vertical-align': 'top'
            },
            cellRenderer: (params: any) => {
              const value = params.value || "-";
              const sourceInfo = params.data?.[`${params.colDef.field}_sourceInfo`];
              return showIndicators
                ? createCellWithIndicator(value, sourceInfo, DEFAULT_DOCUMENT_ID)
                : value;
            }
          });
        }
      });

      return [studyColumn, ...referenceColumns];
    }
  
    // Create column definitions for topic tables
    function createTopicColumnDefs(
      columns: any[],
      grouperStructure?: any,
      isMultiLevel: boolean = false,
      options: { fieldPrefix?: string } = {}
    ): any[] {
      const prefix = options.fieldPrefix ?? "";

      if (grouperStructure?.levels) {
        const allColumns: any[] = [];
        grouperStructure.levels.forEach((level: any) => {
          if (!Array.isArray(level.columns)) {
            return;
          }
          const typeColumn = level.columns.find((c: any) => c.id === 'type');
          const nameColumn = level.columns.find((c: any) => c.id === 'name');
          const columnsInOrder = [typeColumn, nameColumn].filter(Boolean);

          columnsInOrder.forEach((column: any) => {
            const isNameColumn = column.id === 'name';
            const isMultiline = column.multiline === true;
            const columnDef = {
              field: `${prefix}level${level.number}_${column.id}`,
              headerName: column.label,
              width: isMultiline ? 200 : (isNameColumn ? 200 : undefined),
              minWidth: isMultiline ? 200 : (isNameColumn ? 200 : undefined),
              maxWidth: isMultiline ? 200 : (isNameColumn ? 200 : undefined),
              cellRenderer: (params: any) => {
                if (params.value) {
                  return params.value;
                }
                return '-';
              },
              resizable: false,
              lockPosition: true,
              suppressHeaderMenuButton: true,
              suppressHeaderContextMenu: true,
              cellStyle: {
                'padding': '8px 8px',
                'white-space': 'normal',
                'word-wrap': 'break-word',
                'overflow-wrap': 'break-word'
              }
            };
            allColumns.push(columnDef);
          });
        });
        return allColumns;
      }

      return columns.map((column, index) => {
        // Don't pin table columns - only Study and Group columns should be pinned
        const isFixed = false;
        const isMultiline = column.multiline === true;

        let headerName = column.label || column.id;
        if (column.id === 'grouper_name') {
          const grouperNameColumn = grouperStructure?.columns?.find((c: any) => c.id === 'name');
          headerName = column.label || grouperNameColumn?.label || 'Name';
        }

        // Handle grouped columns (like male/female in gender table)
        if (column.type === 'group' && column.fields) {
          // For grouped columns, parent is just a header, children have the actual fields
          const children = column.fields.map((field: any) => ({
            field: `${prefix}${column.id}_${field.id}`,
            headerName: field.label || field.id,
            minWidth: field.multiline === true ? 200 : undefined,
            width: field.multiline === true ? 200 : undefined,
            maxWidth: field.multiline === true ? 200 : undefined,
            resizable: false,
            editable: true, // Enable editing for grouped column fields
            lockPosition: false, // Don't lock table columns
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            cellRenderer: (params: any) => {
              const value = params.value || "-";
              const sourceInfo = params.data?.[`${params.colDef.field}_sourceInfo`];
              return showIndicators
                ? createCellWithIndicator(value, sourceInfo, DEFAULT_DOCUMENT_ID)
                : value;
            },
            cellStyle: field.multiline === true ? {
              'white-space': 'normal',
              'word-wrap': 'break-word',
              'overflow-wrap': 'break-word',
              'padding': '8px 8px',
              'line-height': 'normal',
              'vertical-align': 'top'
            } : {
              'padding': '8px 8px'
            },
            wrapText: true // Enable text wrapping for all columns
          }));

          // Parent column for grouped columns is just a header, no field
          return {
            headerName,
            marryChildren: true,
            children,
            resizable: false,
            lockPosition: false, // Don't lock table columns
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
          };
        }

        // Regular (non-grouped) columns
        // Don't allow editing of grouper_name columns (they're read-only)
        const isGrouperColumn = column.id === 'grouper_name' || column.refTable === 'grouper';
        const columnDef: any = {
          field: `${prefix}${column.id}`,
          headerName,
          pinned: undefined, // Don't pin table columns
          width: isMultiline ? 200 : undefined,
          minWidth: isMultiline ? 200 : undefined,
          maxWidth: isMultiline ? 200 : undefined,
          editable: !isGrouperColumn, // Enable editing except for grouper columns
          cellRenderer: (params: any) => {
            const value = params.value || "-";
            const sourceInfo = params.data?.[`${params.colDef.field}_sourceInfo`];
            return showIndicators
              ? createCellWithIndicator(value, sourceInfo, DEFAULT_DOCUMENT_ID)
              : value;
          },
          suppressSizeToFit: false,
          suppressAutoSize: false,
          resizable: false,
          lockPosition: false, // Don't lock table columns
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          wrapText: true, // Enable text wrapping for all columns (row height calculated by getRowHeight)
          cellStyle: isMultiline ? { 
            'white-space': 'normal',
            'word-wrap': 'break-word',
            'overflow-wrap': 'break-word',
            'padding': '8px 8px',
            'line-height': 'normal',
            'vertical-align': 'top'
          } : {
            'padding': '8px 8px'
          }
        };

        return columnDef;
      });
    }
  
    // Create row data for reference tables
    function createReferenceRowData(rows: any[], studyEntry: StudyExtractEntry): any[] {
      // Get study source info - handle both object-type (values) and table-type (rows with cells)
      let studySourceInfo: any = undefined;
      const studyReference = studyEntry.extract?.references?.find((ref: any) => ref.id === "study");
      
      if (studyReference) {
        if (Array.isArray(studyReference.values)) {
          // Object-type: get from values array
          const studyIdValue = studyReference.values.find?.((value: any) => value.id === "studyid");
          studySourceInfo = studyIdValue?.sourceInfo;
        } else if (Array.isArray(studyReference.rows) && studyReference.rows.length > 0) {
          // Table-type: get from first row's studyid cell
          const firstRow = studyReference.rows[0];
          const studyIdCell = firstRow?.cells?.find((cell: any) => cell.id === "studyid");
          studySourceInfo = studyIdCell?.sourceInfo;
        }
      }

      return rows.map((row, index) => {
        const uniqueRowKey = row.id ? String(row.id) : `${studyEntry.id}_${index + 1}`;
        const rowData: any = {
          [STUDY_COLUMN_FIELD]: studyEntry.name,
          [STUDY_ID_FIELD]: studyEntry.id,
          [STUDY_ROW_KEY_FIELD]: uniqueRowKey,
          [STUDY_SOURCE_FIELD]: studySourceInfo,
        };
        
        // Map cells to row data
        if (row.cells) {
          row.cells.forEach((cell: any) => {
            rowData[cell.id] = cell.value || '-';
            if (cell.sourceInfo) {
              rowData[`${cell.id}_sourceInfo`] = cell.sourceInfo;
            }
          });
        }
        
        return rowData;
      });
    }
  
    // Create row data for topic tables
    function createTopicRowData(
      rows: any[],
      columns: any[],
      grouperData?: any,
      tablesData?: any[],
      isMultiLevel: boolean = false,
      grouperStructure?: any,
      options: { fieldPrefix?: string; studyContext?: any } = {}
    ): any[] {
      const prefix = options.fieldPrefix ?? "";
      const studyContext = options.studyContext;
      // Check if any row has levels structure
      const hasLevels = rows.some(row => row.levels && row.levels.length > 0);
      
      if (isMultiLevel || hasLevels) {
        return createMultiLevelRowData(rows, columns, grouperData, tablesData, { fieldPrefix: prefix, studyContext });
      }
      
      const studyReference = studyContext?.references?.find((ref: any) => ref.id === "study");
      const studyIdValue = studyReference?.values?.find?.((value: any) => value.id === "studyid");
      const studySourceInfo = studyIdValue?.sourceInfo;

      return rows.map((row, rowIndex) => {
        const rowData: any = { id: row.id };
        rowData[STUDY_SOURCE_FIELD] = studySourceInfo;
        
        columns.forEach(column => {
          const cell = row.cells?.find((c: any) => c.id === column.id);
          const fieldName = `${prefix}${column.id}`;
          
          if (cell?.ref && cell?.refID) {
            // Handle cell references (like in grouper table)
            rowData[fieldName] = getReferenceValue(cell.ref, cell.refID, studyContext);
            if (cell.sourceInfo) {
              rowData[`${fieldName}_sourceInfo`] = cell.sourceInfo;
            }
          } else if (column.refTable) {
            // Handle column references (like grouper_name in data tables)
            rowData[fieldName] = resolveColumnReference(
              column,
              row.id,
              rowIndex,
              grouperData,
              tablesData || [],
              grouperStructure
            );
            const referenceSourceInfo = grouperData
              ? (() => {
                  const referenceGrouperRow = Array.isArray(grouperData)
                    ? grouperData.find((g: any) => String(g.grouperId ?? g.id ?? '') === String(row.id))
                    : null;
                  const matchingCell = referenceGrouperRow?.cells?.find?.((c: any) => c.id === column.refID);
                  return matchingCell?.sourceInfo;
                })()
              : undefined;
            if (referenceSourceInfo) {
              rowData[`${fieldName}_sourceInfo`] = referenceSourceInfo;
            }
          } else if (column.type === 'group' && column.fields) {
            // Handle column grouping - map individual cells to grouped fields
            column.fields.forEach((field: any) => {
              const fieldCell = row.cells?.find((c: any) => c.id === field.id);
              if (fieldCell) {
                const groupedFieldName = `${prefix}${column.id}_${field.id}`;
                rowData[groupedFieldName] = fieldCell.value || '-';
                if (fieldCell.sourceInfo) {
                  rowData[`${groupedFieldName}_sourceInfo`] = fieldCell.sourceInfo;
                }
              }
            });
          } else {
            // Handle regular cell data
            rowData[fieldName] = cell?.value || '-';
            if (cell?.sourceInfo) {
              rowData[`${fieldName}_sourceInfo`] = cell.sourceInfo;
            }
          }
        });
        
        return rowData;
      });
    }
  
    // Helper to create multi-level row data with row spanning
    function createMultiLevelRowData(
      rows: any[],
      columns: any[],
      grouperData?: any,
      tablesData?: any[],
      options: { fieldPrefix?: string; studyContext?: any } = {}
    ): any[] {
      const prefix = options.fieldPrefix ?? "";
      const studyContext = options.studyContext;
      const result: any[] = [];
      
      // Process hierarchical data structure
      rows.forEach(row => {
        const rowData: any = { id: row.id };
        
        // Process each level in the hierarchical structure
        if (row.levels) {
          row.levels.forEach((level: any) => {
            level.cells.forEach((cell: any) => {
              const fieldName = `${prefix}level${level.number}_${cell.id}`;
              
              if (cell.ref && cell.refID) {
                rowData[fieldName] = getReferenceValue(cell.ref, cell.refID, studyContext);
              } else {
                rowData[fieldName] = cell.value || '-';
              }
            });
          });
        }
        
        result.push(rowData);
      });
      
      return result;
    }
  
    // Helper to get reference structure
    function getReferenceStructure(refId: string): any {
      return projectStructure?.references?.find((r: any) => r.id === refId);
    }
  
    // Helper to get topic structure
    function getTopicStructure(topicId: string): any {
      return projectStructure?.topics?.find((t: any) => t.id === topicId);
    }
  
    // Excel export styles - dark gray headers with white text and white borders
    const excelStyles = [
      {
        id: 'header',
        alignment: {
          vertical: 'Center' as const,
          horizontal: 'Center' as const
        },
        font: {
          color: '#FFFFFF', // White text
          bold: true
        },
        interior: {
          color: '#404040', // Dark gray background
          pattern: 'Solid' as const
        },
        borders: {
          borderBottom: {
            color: '#FFFFFF',
            lineStyle: 'Continuous' as const,
            weight: 1
          },
          borderLeft: {
            color: '#FFFFFF',
            lineStyle: 'Continuous' as const,
            weight: 1
          },
          borderRight: {
            color: '#FFFFFF',
            lineStyle: 'Continuous' as const,
            weight: 1
          },
          borderTop: {
            color: '#FFFFFF',
            lineStyle: 'Continuous' as const,
            weight: 1
          }
        }
      },
      {
        id: 'headerGroup',
        alignment: {
          vertical: 'Center' as const,
          horizontal: 'Center' as const
        },
        font: {
          color: '#FFFFFF', // White text
          bold: true
        },
        interior: {
          color: '#404040', // Dark gray background
          pattern: 'Solid' as const
        },
        borders: {
          borderBottom: {
            color: '#FFFFFF',
            lineStyle: 'Continuous' as const,
            weight: 1
          },
          borderLeft: {
            color: '#FFFFFF',
            lineStyle: 'Continuous' as const,
            weight: 1
          },
          borderRight: {
            color: '#FFFFFF',
            lineStyle: 'Continuous' as const,
            weight: 1
          },
          borderTop: {
            color: '#FFFFFF',
            lineStyle: 'Continuous' as const,
            weight: 1
          }
        }
      }
    ] as any;
  
    // Default grid options (matching ExtractItems2.svelte)
    const defaultGridOptions: any = {
      suppressCellFocus: true,
      suppressColumnVirtualisation: true,
      suppressRowVirtualisation: true,
      autoSizeStrategy: {
        type: 'fitCellContents' as const,
      },
      suppressRowHoverHighlight: false, // Enable row hover highlight for styling
      columnHoverHighlight: true, // Enable column hover highlight
      suppressColumnMoveAnimation: true,
      suppressContextMenu: true,
      suppressMenuHide: true,
      headerHeight: 32,
      domLayout: 'normal' as const, // Use normal layout with getRowHeight for content fitting
      enableCellSpan: true,
      cellSelection: false,
      loading: false,
      suppressMovableColumns: true,
      // Excel export styles - dark gray headers with white text and white borders
      excelStyles: excelStyles as any,
      // Enable alternating row colors
      getRowClass: (params: any) => {
        if (params.node.rowIndex !== null && params.node.rowIndex !== undefined) {
          return params.node.rowIndex % 2 === 1 ? 'ag-row-odd' : 'ag-row-even';
        }
        return '';
      },
      defaultColDef: {
        sortable: false,
        filter: false, // Will be overridden for topic grids
        lockPosition: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        headerClass: 'project-extract-header-left',
        wrapText: true // Enable text wrapping for all columns
      }
    };
  
    // Initialize grid with data
    function destroyGrid() {
      if (gridInstance && gridApi && !gridApi.isDestroyed()) {
        gridInstance.destroy();
      }
      gridInstance = null;
      gridApi = null;
      
      // Clean up stored grid API for current tab
      if (activeTab) {
        gridApisByTab.delete(activeTab);
      }
    }
    
    // Initialize hidden grid for export (doesn't affect visible grid)
    function initializeHiddenGrid(columnDefs: any[], rowData: any[]) {
      if (!hiddenGridDiv) {
        logWarning('Hidden grid div not available', "ProjectExtract", undefined);
        return;
      }
      
      if (columnDefs.length === 0) {
        return;
      }
      
      // Clean up existing hidden grid
      if (hiddenGridInstance && hiddenGridApi && !hiddenGridApi.isDestroyed()) {
        hiddenGridInstance.destroy();
      }
      hiddenGridInstance = null;
      hiddenGridApi = null;
      
      const gridOptions: any = {
        ...defaultGridOptions,
        columnDefs: columnDefs,
        rowData: rowData,
        loading: false,
        // Simplified options for hidden grid - no filters, no sidebars
        enableFilterHandlers: false,
        sideBar: false,
        overlayNoRowsTemplate: '<div></div>',
        onGridReady: (params: any) => {
          hiddenGridApi = params.api;
        }
      };
      
      hiddenGridInstance = createGrid(hiddenGridDiv, gridOptions);
    }
    
    // Load reference data into hidden grid
    async function loadReferenceDataToHiddenGrid(referenceId: string): Promise<{ columnDefs: any[], rowData: any[] }> {
      await ensureStoresLoaded();
      
      const refStructure = projectStructure?.references?.find((r: any) => r.id === referenceId);
      
      if (!refStructure) {
        return { columnDefs: [], rowData: [] };
      }

      // Handle object-type references (like "study")
      if (refStructure.type === 'object') {
        const columnDefs: any[] = [];
        columnDefs.push(createStudyColumnDef("Study"));
        const rowData: any[] = [];

        // Process fields to create column definitions and row data
        const processFields = (fields: any[], parentPath: string = '') => {
          fields.forEach((field: any) => {
            if (field.type === 'group' && Array.isArray(field.fields)) {
              // Handle grouped fields - create a parent column with children
              const groupColumns = field.fields.map((subField: any) => {
                const fieldPath = parentPath ? `${parentPath}.${field.id}.${subField.id}` : `${field.id}.${subField.id}`;
                return {
                  headerName: subField.label || subField.id,
                  field: fieldPath,
                  colId: fieldPath,
                  pinned: undefined,
                  width: subField.multiline ? 200 : undefined,
                  minWidth: subField.multiline ? 200 : undefined,
                  maxWidth: subField.multiline ? 200 : undefined,
                  suppressSizeToFit: subField.multiline,
                  suppressAutoSize: subField.multiline,
                  resizable: false,
                  sortable: false,
                  filter: false,
                  lockPosition: false,
                  suppressHeaderMenuButton: true,
                  suppressHeaderContextMenu: true,
                  wrapText: true,
                  autoHeight: true,
                  cellStyle: {
                    'white-space': 'normal',
                    'word-wrap': 'break-word',
                    'overflow-wrap': 'break-word',
                    'padding': '8px 8px',
                    'line-height': 'normal',
                    'vertical-align': 'top'
                  }
                };
              });

              if (groupColumns.length > 0) {
                columnDefs.push({
                  headerName: field.label || field.id,
                  marryChildren: true,
                  children: groupColumns,
                  resizable: false,
                  lockPosition: false,
                  suppressHeaderMenuButton: true,
                  suppressHeaderContextMenu: true,
                });
              }
            } else {
              // Handle regular fields
              const fieldPath = parentPath ? `${parentPath}.${field.id}` : field.id;
              columnDefs.push({
                headerName: field.label || field.id,
                field: fieldPath,
                colId: fieldPath,
                pinned: undefined,
                width: field.multiline ? 200 : undefined,
                minWidth: field.multiline ? 200 : undefined,
                maxWidth: field.multiline ? 200 : undefined,
                suppressSizeToFit: field.multiline,
                suppressAutoSize: field.multiline,
                resizable: false,
                sortable: false,
                filter: false,
                lockPosition: false,
                suppressHeaderMenuButton: true,
                suppressHeaderContextMenu: true,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                  'white-space': 'normal',
                  'word-wrap': 'break-word',
                  'overflow-wrap': 'break-word',
                  'padding': '8px 8px',
                  'line-height': 'normal',
                  'vertical-align': 'top'
                }
              });
            }
          });
        };

        if (refStructure.fields && Array.isArray(refStructure.fields)) {
          processFields(refStructure.fields);
        }

        studyExtractEntries.forEach((entry) => {
          const reference = entry.extract?.references?.find((ref: any) => ref.id === referenceId);
          if (!reference) {
            return;
          }
          
          const rowDataItem: any = {
            [STUDY_COLUMN_FIELD]: entry.name,
            [STUDY_ID_FIELD]: entry.id,
            [STUDY_ROW_KEY_FIELD]: `${entry.id}_${referenceId}`,
          };

          // Process fields from the reference object
          if (refStructure.fields && Array.isArray(refStructure.fields)) {
            refStructure.fields.forEach((field: any) => {
              if (field.type === 'group' && Array.isArray(field.fields)) {
                field.fields.forEach((subField: any) => {
                  const fieldPath = `${field.id}.${subField.id}`;
                  const value = reference.values?.find((v: any) => v.id === subField.id);
                  rowDataItem[fieldPath] = value?.value || '-';
                });
              } else {
                const value = reference.values?.find((v: any) => v.id === field.id);
                rowDataItem[field.id] = value?.value || '-';
              }
            });
          }

          rowData.push(rowDataItem);
        });

        return { columnDefs, rowData };
      }

      // Handle table-type references
      if (refStructure.type !== 'table') {
        return { columnDefs: [], rowData: [] };
      }

      const columnDefs = createReferenceColumnDefs(refStructure.columns || []);
      const rowData: any[] = [];

      studyExtractEntries.forEach((entry) => {
        const reference = entry.extract?.references?.find((ref: any) => ref.id === referenceId);
        if (!reference || !Array.isArray(reference.rows)) {
          return;
        }
        const studyRows = createReferenceRowData(reference.rows, entry);
        rowData.push(...studyRows);
      });

      return { columnDefs, rowData };
    }
    
    // Load topic data into hidden grid - returns columnDefs and rowData
    async function loadTopicDataToHiddenGrid(topicId: string): Promise<{ columnDefs: any[], rowData: any[] }> {
      await ensureStoresLoaded();
      
      const structureTopic = projectStructure?.topics?.find((st: any) => st.id === topicId);
      
      if (!structureTopic) {
        return { columnDefs: [], rowData: [] };
      }
      
      // Reuse the existing createCombinedTopicTable logic but return data instead of initializing grid
      // We'll need to extract the columnDefs and rowData building logic
      // For now, let's create a simplified version that builds the data
      const columnDefs: any[] = [];
      columnDefs.push(createStudyColumnDef("Study"));

      const hasGrouper = Boolean(structureTopic.grouper);
      const grouperLevels = Array.isArray(structureTopic.grouper?.levels) 
        ? structureTopic.grouper.levels 
        : [];
      const isMultiLevel = grouperLevels.length > 1;
      
      // Add grouper columns
      if (hasGrouper) {
        if (isMultiLevel) {
          grouperLevels.forEach((level: any, levelIndex: number) => {
            const levelNumber = String(level.level ?? level.number ?? levelIndex + 1);
            const levelLabel = level.label || `Level ${levelNumber}`;
            const fieldName = `group_level_${levelNumber}`;
            
            columnDefs.push({
              headerName: levelLabel,
              marryChildren: true,
              children: [{
                field: fieldName,
                headerName: levelLabel,
                pinned: 'left',
                width: 200,
                minWidth: 200,
                maxWidth: 280,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                sortable: false,
                filter: false,
                lockPosition: true,
                suppressHeaderMenuButton: true,
                suppressHeaderContextMenu: true,
                cellStyle: {
                  'white-space': 'normal',
                  'word-wrap': 'break-word',
                  'overflow-wrap': 'break-word',
                  'padding': '8px 8px',
                  'font-weight': 'bold'
                }
              }]
            });
          });
        } else {
          columnDefs.push({
            headerName: structureTopic.grouper?.label || "Group",
            marryChildren: true,
            children: [{
              field: 'group',
              headerName: 'Group',
              pinned: 'left',
              width: 200,
              minWidth: 200,
              maxWidth: 280,
              suppressSizeToFit: true,
              suppressAutoSize: true,
              resizable: false,
              sortable: false,
              filter: false,
              lockPosition: true,
              suppressHeaderMenuButton: true,
              suppressHeaderContextMenu: true,
              cellStyle: {
                'white-space': 'normal',
                'word-wrap': 'break-word',
                'overflow-wrap': 'break-word',
                'padding': '8px 8px',
                'font-weight': 'bold'
              }
            }]
          });
        }
        
        // Add root-level grouper columns (e.g., sample_size, analysis_set, etc.) after Group columns
        // These should scroll horizontally with table columns, not be pinned
        const rootGrouperColumns = projectStructure?.grouper?.columns || [];
        if (rootGrouperColumns.length > 0 && hasGrouper) {
          rootGrouperColumns.forEach((columnDef: any) => {
            const lookupRenderer = getLookupCellRenderer(columnDef, projectStructure);
            const isMultiline = columnDef.multiline === true;
            columnDefs.push({
              field: columnDef.id,
              headerName: (columnDef.label || columnDef.id).toUpperCase(),
              minWidth: isMultiline ? 200 : 100,
              width: isMultiline ? 200 : undefined, // Fixed width for multiline, auto-size for others
              maxWidth: isMultiline ? 200 : undefined,
              suppressSizeToFit: isMultiline, // Prevent auto-sizing for multiline columns
              suppressAutoSize: isMultiline,
              editable: false,
              wrapText: isMultiline, // Enable text wrapping for multiline columns
              autoHeight: false, // Disable autoHeight - use getRowHeight instead
              cellRenderer: (params: any) => {
                let displayValue = params.value || '-';
                if (lookupRenderer && displayValue !== '-') {
                  displayValue = lookupRenderer(params) || '-';
                }
                const sourceInfo = params.data?.[`${columnDef.id}_sourceInfo`];
                return showIndicators
                  ? createCellWithIndicator(displayValue, sourceInfo, DEFAULT_DOCUMENT_ID)
                  : displayValue;
              },
              sortable: false,
              filter: false,
              resizable: false,
              lockPosition: false, // Allow horizontal scrolling
              suppressHeaderMenuButton: true,
              suppressHeaderContextMenu: true,
              cellStyle: isMultiline ? {
                'white-space': 'normal',
                'word-wrap': 'break-word',
                'overflow-wrap': 'break-word',
                'padding': '8px 8px',
                'line-height': 'normal',
                'vertical-align': 'top'
              } : {
                'padding': '8px 8px'
              }
            });
          });
        }
      }

      // Add all table columns
      const tableStructures = Array.isArray(structureTopic.tables) ? structureTopic.tables : [];
      tableStructures.forEach((tableStructure: any) => {
        const tableColumns = createTopicColumnDefs(
          tableStructure.columns || [],
          undefined,
          false,
          { fieldPrefix: `${tableStructure.id}__` }
        ).filter((columnDef: any) => {
          if (typeof columnDef.field === 'string' && columnDef.field.endsWith('__grouper_name')) {
            return false;
          }
          return true;
        });

        if (tableColumns.length > 0) {
          columnDefs.push({
            headerName: tableStructure.label || toTitleCase(tableStructure.id),
            marryChildren: true,
            children: tableColumns,
            resizable: false,
            lockPosition: false,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
          });
        }
      });

      // Build row data - this is complex, so we'll reuse the existing logic by temporarily
      // calling the actual loadTopicData but capturing the result
      // Actually, better to call createCombinedTopicTable logic directly
      // For now, let's use a workaround: temporarily set activeTab and call loadTopicData
      // then extract from the grid, but that's what we're trying to avoid
      
      // Instead, let's extract the row data building logic
      const rowData: any[] = [];
      
      // Get root-level grouper columns for data population (defined at function scope)
      const rootGrouperColumnsForData = projectStructure?.grouper?.columns || [];
      
      studyExtractEntries.forEach((entry) => {
        const topic = entry.extract?.topics?.find((t: any) => t.id === topicId);
        if (!topic) {
          return;
        }
        
        let grouperRows: any[] = [];
        if (hasGrouper && topic.grouper) {
          if (Array.isArray(topic.grouper)) {
            try {
              grouperRows = ensureGrouperArrayLocal(topic.grouper, `topic ${topicId}`);
            } catch (error) {
              grouperRows = [null];
            }
          } else if (topic.grouper && Array.isArray(topic.grouper.rows)) {
            grouperRows = topic.grouper.rows;
          } else {
            grouperRows = [null];
          }
        } else {
          grouperRows = [null];
        }
        
        // Pre-process table data
        const tableRowDataByTable = new Map<string, Map<string, any>>();
        tableStructures.forEach((tableStructure: any) => {
          const table = Array.isArray(topic.tables)
            ? topic.tables.find((t: any) => t.id === tableStructure.id)
            : null;
          const tableRows = table?.rows || [];
          
          const tableRowData = createTopicRowData(
            tableRows,
            tableStructure.columns || [],
            grouperRows.length > 0 ? grouperRows : undefined,
            topic.tables,
            false,
            structureTopic.grouper,
            { fieldPrefix: `${tableStructure.id}__`, studyContext: entry.extract }
          );

          const rowMap = new Map<string, any>();
          tableRowData.forEach((row: any, idx: number) => {
            const originalRow = tableRows[idx];
            const rowId = row.id || originalRow?.id || originalRow?.grouperId;
            if (rowId) {
              const { id, ...rest } = row;
              const rowIdKey = String(rowId).trim();
              rowMap.set(rowIdKey, rest);
            }
          });
          tableRowDataByTable.set(tableStructure.id, rowMap);
        });

        // Sort grouper rows
        const sortedGrouperRows = grouperRows.map((row, index) => ({ row, originalIndex: index }))
          .sort((a, b) => {
            const aRow = getNumericOrderValue(a.row, a.originalIndex + 1);
            const bRow = getNumericOrderValue(b.row, b.originalIndex + 1);
            return aRow - bRow;
          })
          .map(item => item.row);
        
        // Create rows
        sortedGrouperRows.forEach((grouperRow: any, rowIndex: number) => {
          const rowKeyBase = grouperRow?.grouperId || grouperRow?.id 
            ? String(grouperRow.grouperId || grouperRow.id) 
            : `row_${rowIndex}`;
          
          const orderNumber = getNumericOrderValue(grouperRow, rowIndex + 1);
          
          const rowDataItem: any = {
            [STUDY_COLUMN_FIELD]: entry.name,
            [STUDY_ID_FIELD]: entry.id,
            [STUDY_ROW_KEY_FIELD]: `${entry.id}_${topicId}_${rowKeyBase}`,
            _grouperOrderNumber: orderNumber,
          };

          // Set group name(s)
          if (hasGrouper) {
            if (isMultiLevel) {
              grouperLevels.forEach((level: any, levelIndex: number) => {
                const levelNumber = String(level.level ?? level.number ?? levelIndex + 1);
                const fieldName = `group_level_${levelNumber}`;
                const refs = getLevelRefsLocal(grouperRow, levelNumber);
                const levelRefs = level.refs || [];
                const levelSources = (level.sources || []).filter((s: any) => s?.type === 'reference');
                const refDefs = levelRefs.length > 0 ? levelRefs : levelSources;
                const displayParts: string[] = [];
                
                refDefs.forEach((refDef: any) => {
                  const refId = refs[displayParts.length];
                  if (!refId) return;
                  const refTable = refDef.refTable || refDef.refId;
                  if (refTable) {
                    const refValue = getReferenceValue(refTable, refId, entry.extract);
                    if (refValue && refValue !== '-') {
                      displayParts.push(refValue);
                    }
                  }
                });
                
                let displayValue = displayParts.length > 0 ? displayParts.join(' ● ') : '-';
                if ((!displayValue || displayValue === '-') && levelNumber === '1') {
                  const customValue = getCustomValue(grouperRow);
                  displayValue = customValue || 'Study';
                }
                
                rowDataItem[fieldName] = displayValue;
              });
            } else {
              const refs = getLevelRefsLocal(grouperRow, '1');
              const level = grouperLevels[0] || structureTopic.grouper;
              const levelRefs = level?.refs || [];
              const levelSources = (level?.sources || []).filter((s: any) => s?.type === 'reference');
              const refDefs = levelRefs.length > 0 ? levelRefs : levelSources;
              const displayParts: string[] = [];
              
              refDefs.forEach((refDef: any) => {
                const refId = refs[displayParts.length];
                if (!refId) return;
                const refTable = refDef.refTable || refDef.refId;
                if (refTable) {
                  const refValue = getReferenceValue(refTable, refId, entry.extract);
                  if (refValue && refValue !== '-') {
                    displayParts.push(refValue);
                  }
                }
              });
              
              let displayValue = displayParts.length > 0 ? displayParts.join(' ● ') : '-';
              if (!displayValue || displayValue === '-') {
                const customValue = getCustomValue(grouperRow);
                displayValue = customValue || 'Study';
              }
              
              rowDataItem['group'] = displayValue;
            }
            
            // Add root-level grouper column values (e.g., sample_size, analysis_set, etc.)
            // Read grouper cell values from cells array
            const grouperCells = Array.isArray(grouperRow?.cells) ? grouperRow.cells : [];
            rootGrouperColumnsForData.forEach((columnDef: any) => {
              const cell = grouperCells.find((c: any) => c.id === columnDef.id);
              const cellValue = cell?.value;
              // Only set to '-' if value is explicitly empty/null/undefined, otherwise use the actual value
              const finalValue = (cellValue === null || cellValue === undefined || cellValue === '') ? '-' : cellValue;
              rowDataItem[columnDef.id] = finalValue;
              // Store sourceInfo if available
              if (cell?.sourceInfo) {
                rowDataItem[`${columnDef.id}_sourceInfo`] = cell.sourceInfo;
              }
            });
          }

          // Add table data
          const rowIdKey = String(grouperRow?.grouperId || grouperRow?.id || rowKeyBase).trim();
          tableStructures.forEach((tableStructure: any) => {
            const tableRowMap = tableRowDataByTable.get(tableStructure.id);
            if (tableRowMap) {
              const tableRow = tableRowMap.get(rowIdKey);
              if (tableRow) {
                Object.assign(rowDataItem, tableRow);
              }
            }
          });

          rowData.push(rowDataItem);
        });
      });

      return { columnDefs, rowData };
    }
    
    // Export all tabs to Excel with multiple sheets
    async function exportAllTabsToExcel() {
      if (isExporting) return; // Prevent multiple simultaneous exports
      
      try {
        isExporting = true;
        exportModalOpen = true;
        exportProgress = 'Preparing export...';
        
        // Store the original active tab - we won't change it
        const originalTab = activeTab;
        
        // Extract data from each tab using the hidden grid
        const sheetsData: Array<{ name: string; columnDefs: any[]; rowData: any[] }> = [];
        
        for (let i = 0; i < mainSections.length; i++) {
          const section = mainSections[i];
          exportProgress = `Loading ${section.label || section.id} (${i + 1}/${mainSections.length})...`;
          
          try {
            let loadedColumnDefs: any[] = [];
            let loadedRowData: any[] = [];
            
            // Load data into hidden grid based on section type
            if (section.type === 'reference') {
              const data = await loadReferenceDataToHiddenGrid(section.sourceId);
              loadedColumnDefs = data.columnDefs;
              loadedRowData = data.rowData;
            } else if (section.type === 'topic') {
              const data = await loadTopicDataToHiddenGrid(section.sourceId);
              loadedColumnDefs = data.columnDefs;
              loadedRowData = data.rowData;
            }
            
            // Initialize hidden grid with the data
            // Use the EXACT same columnDefs that were built - don't extract from grid API
            // This ensures grouped columns are preserved correctly
            if (loadedColumnDefs.length > 0 && loadedRowData.length > 0) {
              initializeHiddenGrid(loadedColumnDefs, loadedRowData);
              
              // Wait for grid to be ready
              await new Promise(resolve => setTimeout(resolve, 200));
              
              // Verify grid has data
              if (hiddenGridApi && !hiddenGridApi.isDestroyed()) {
                const rowCount = hiddenGridApi.getDisplayedRowCount();
                
                if (rowCount > 0) {
                  // Use the ORIGINAL columnDefs that were used to initialize the grid
                  // Don't extract from grid API - this preserves the exact structure including grouped columns
                  const sheetName = section.label || section.id;
                  const sanitizedSheetName = sheetName
                    .replace(/[\\\/\?\*\[\]:]/g, '_')
                    .substring(0, 31);
                  
                  // Extract row data from the grid
                  const extractedRowData: any[] = [];
                  hiddenGridApi.forEachNodeAfterFilterAndSort((node: any) => {
                    if (node.data) {
                      extractedRowData.push(node.data);
                    }
                  });
                  
                  // Store using the original columnDefs (which preserve grouped structure)
                  sheetsData.push({
                    name: sanitizedSheetName,
                    columnDefs: loadedColumnDefs, // Use original columnDefs, not extracted ones
                    rowData: extractedRowData
                  });
                }
              }
            }
          } catch (sectionError) {
            logError(sectionError, "ProjectExtract", undefined, { message: `Error loading section ${section.id}` });
          }
        }
        
        if (sheetsData.length === 0) {
          exportModalOpen = false;
          isExporting = false;
          alert('No grids with data available to export.');
          return;
        }
        
        // According to AG Grid docs, we need to:
        // 1. Call getSheetDataForExcel() on each grid to get sheet data
        // 2. Collect all results in an array
        // 3. Call exportMultipleSheetsAsExcel({ data: array, fileName: "..." })
        
        const spreadsheets: any[] = [];
        
        // Collect sheet data from each hidden grid instance
        for (let i = 0; i < sheetsData.length; i++) {
          const sheet = sheetsData[i];
          exportProgress = `Preparing ${sheet.name} (${i + 1}/${sheetsData.length})...`;
          
          // Re-initialize hidden grid with this sheet's data
          if (sheet.columnDefs.length > 0 && sheet.rowData.length > 0) {
            initializeHiddenGrid(sheet.columnDefs, sheet.rowData);
            
            // Wait for grid to be ready - need to wait for onGridReady callback
            let gridReady = false;
            let attempts = 0;
            while (!gridReady && attempts < 20) {
              await new Promise(resolve => setTimeout(resolve, 100));
              if (hiddenGridApi && !hiddenGridApi.isDestroyed()) {
                // Check if grid has data loaded
                const rowCount = hiddenGridApi.getDisplayedRowCount();
                if (rowCount > 0 || sheet.rowData.length === 0) {
                  gridReady = true;
                }
              }
              attempts++;
            }
            
            if (hiddenGridApi && !hiddenGridApi.isDestroyed()) {
              try {
                // Call getSheetDataForExcel() with the sheet name
                // According to AG Grid docs, grouped columns are automatically preserved
                // We ensure all columns are visible (not collapsed) by using the grid's actual structure
                const sheetData = (hiddenGridApi as any).getSheetDataForExcel({ 
                  sheetName: sheet.name,
                  // Ensure all columns are exported (including collapsed groups if needed)
                  // Grouped columns should be automatically preserved
                  allColumns: false // Only export visible columns (default), grouped structure is preserved
                });
                
                if (sheetData) {
                  spreadsheets.push(sheetData);
                } else {
                  logWarning(`No sheet data returned for ${sheet.name}`, "ProjectExtract", undefined);
                }
              } catch (e) {
                logError(e, "ProjectExtract", undefined, { message: `getting sheet data for ${sheet.name}` });
              }
            } else {
              logWarning(`Hidden grid API not available for ${sheet.name}`, "ProjectExtract", undefined);
            }
          }
        }
        
        if (spreadsheets.length === 0) {
          throw new Error('No sheets to export');
        }
        
        // Use the first grid API (or hidden grid API) to export
        let exportApi = hiddenGridApi;
        if (!exportApi || exportApi.isDestroyed()) {
          exportApi = gridApi;
        }
        
        // If still no valid API, initialize one more time with the first sheet
        if (!exportApi || exportApi.isDestroyed()) {
          if (sheetsData.length > 0 && sheetsData[0].columnDefs.length > 0 && sheetsData[0].rowData.length > 0) {
            initializeHiddenGrid(sheetsData[0].columnDefs, sheetsData[0].rowData);
            await new Promise(resolve => setTimeout(resolve, 500));
            exportApi = hiddenGridApi;
          }
        }
        
        if (exportApi && !exportApi.isDestroyed() && typeof (exportApi as any).exportMultipleSheetsAsExcel === 'function') {
          try {
            
            const fileName = `${projectDetails?.name || projectTitle || 'export'}_all_sheets.xlsx`;
            
            // AG Grid format: { data: [...getSheetDataForExcel results...], fileName: "..." }
            (exportApi as any).exportMultipleSheetsAsExcel({
              data: spreadsheets,
              fileName: fileName
            });
            
            exportProgress = 'Export complete!';
            // Keep modal open for 3 seconds after completion
            await new Promise(resolve => setTimeout(resolve, 3000));
            exportModalOpen = false;
          } catch (multiSheetError) {
            logError(multiSheetError, "ProjectExtract", undefined, { message: "Multi-sheet export failed" });
            exportModalOpen = false;
            alert('Failed to export multiple sheets. Error: ' + (multiSheetError as Error).message);
          }
        } else {
          logError(new Error('exportMultipleSheetsAsExcel not available or grid destroyed'), "ProjectExtract", undefined, {
            hasExportApi: !!exportApi,
            isDestroyed: exportApi?.isDestroyed(),
            hasMethod: exportApi ? typeof (exportApi as any).exportMultipleSheetsAsExcel : 'no api'
          });
          exportModalOpen = false;
          alert('Multi-sheet export is not available. Please ensure AG Grid Enterprise is properly configured.');
        }
      } catch (error) {
        logError(error, "ProjectExtract", undefined, { message: "Error exporting to Excel" });
        exportModalOpen = false;
        alert('Failed to export to Excel. Please try again.');
      } finally {
        isExporting = false;
        exportProgress = '';
      }
    }
  
    // Handle cell value changes in the grid
    function handleCellValueChanged(params: any) {
      const { data, colDef, newValue, oldValue } = params;
      
      // Skip if value hasn't actually changed
      if (newValue === oldValue) {
        return;
      }
      
      // Get current section to determine if it's a reference or topic
      const currentSection = mainSections.find((s) => s.id === activeTab);
      if (!currentSection) {
        logWarning('Cannot update cell - no active section', "ProjectExtract", undefined);
        return;
      }
      
      // Get study ID from row data
      const studyId = data?.[STUDY_ID_FIELD];
      if (!studyId) {
        logWarning('Cannot update cell - missing study ID', "ProjectExtract", undefined);
        return;
      }
      
      // Find the study entry
      const studyEntry = studyExtractEntries.find((entry) => entry.id === studyId);
      if (!studyEntry) {
        logWarning(`Cannot update cell - study not found: ${studyId}`, "ProjectExtract", undefined);
        return;
      }
      
      // Get user info
      const currentUser = $userStore?.user;
      const userId = currentUser?.user_id || currentUser?.oid || 'unknown';
      const userName = currentUser?.name || currentUser?.email || 'Unknown User';
      
      // Convert empty string to '-' for storage
      const valueToSave = newValue === '' || newValue === null || newValue === undefined ? '-' : newValue;
      
      let updatedCellSourceInfo: any = null;
      
      if (currentSection.type === 'reference') {
        updatedCellSourceInfo = handleReferenceCellChange(
          studyEntry,
          currentSection.sourceId,
          data,
          colDef,
          valueToSave,
          userId,
          userName
        );
      } else if (currentSection.type === 'topic') {
        updatedCellSourceInfo = handleTopicCellChange(
          studyEntry,
          currentSection.sourceId,
          data,
          colDef,
          valueToSave,
          userId,
          userName
        );
      }
      
      // Update grid cell with new sourceInfo
      if (gridApi && params.node && updatedCellSourceInfo) {
        const updatedRowData = {
          ...data,
          [colDef.field]: valueToSave,
          [`${colDef.field}_sourceInfo`]: updatedCellSourceInfo
        };
        gridApi.applyTransaction({ update: [updatedRowData] });
        gridApi.refreshCells({
          rowNodes: [params.node],
          columns: [colDef.field],
          force: true
        });
      }
    }
    
    // Handle reference cell changes
    // Returns the updated sourceInfo for the cell, or null if update failed
    function handleReferenceCellChange(
      studyEntry: StudyExtractEntry,
      referenceId: string,
      rowData: any,
      colDef: any,
      value: any,
      userId: string,
      userName: string
    ): any {
      const refStructure = projectStructure?.references?.find((r: any) => r.id === referenceId);
      if (!refStructure) {
        logWarning(`Reference structure not found: ${referenceId}`, "ProjectExtract", undefined);
        return null;
      }
      
      // Handle object-type references (like "study")
      if (refStructure.type === 'object') {
        // For object-type references, the row key is `${studyId}_${referenceId}`
        // We need to update the values array
        const reference = studyEntry.extract?.references?.find((r: any) => r.id === referenceId);
        if (!reference) {
          logWarning(`Reference not found: ${referenceId}`, "ProjectExtract", undefined);
          return;
        }
        
        // Find the field in the structure
        const field = findFieldInStructure(refStructure.fields || [], colDef.field);
        if (!field) {
          logWarning(`Field not found for column: ${colDef.field}`, "ProjectExtract", undefined);
          return;
        }
        
        // Update the value in the values array
        if (!Array.isArray(reference.values)) {
          reference.values = [];
        }
        
        let valueItem = reference.values.find((v: any) => v.id === field.id);
        if (!valueItem) {
          valueItem = { id: field.id };
          reference.values.push(valueItem);
        }
        
        valueItem.value = value;
        const now = new Date().toISOString();
        valueItem.sourceInfo = {
          sourceType: 'user',
          sourceID: userId,
          sourceName: userName,
          sourceDate: now
        };
        
        // Update the study entry
        const updatedExtract = JSON.parse(JSON.stringify(studyEntry.extract));
        const updatedReference = updatedExtract.references?.find((r: any) => r.id === referenceId);
        if (updatedReference) {
          updatedReference.values = reference.values;
        }
        studyEntry.extract = updatedExtract;
        
        // Save to localStorage
        const storageKey = `study_demo_${currentDatasetKey}_${studyExtractEntries.indexOf(studyEntry) + 1}`;
        saveTargetExtract('study', storageKey, updatedExtract);
        
        return valueItem.sourceInfo;
      }
      
      // Handle table-type references
      if (refStructure.type !== 'table') {
        logWarning(`Unknown reference type: ${refStructure.type}`, "ProjectExtract", undefined);
        return null;
      }
      
      // Parse row key to get row ID
      // For table-type references, row key is `${studyId}_${index + 1}` or uses row.id
      const rowKey = rowData[STUDY_ROW_KEY_FIELD];
      if (!rowKey) {
        logWarning('Cannot update cell - missing row key', "ProjectExtract", undefined);
        return;
      }
      
      // Find the row ID - it's stored in the original row data
      // The rowKey format is `${studyId}_${referenceId}` for object types
      // For table types, we need to find the row by matching the row data
      const reference = studyEntry.extract?.references?.find((r: any) => r.id === referenceId);
      if (!reference || !Array.isArray(reference.rows)) {
        logWarning(`Reference not found or has no rows: ${referenceId}`, "ProjectExtract", undefined);
        return;
      }
      
      // Find the row - try to match by rowKey pattern or by position
      // The rowKey might be `${studyId}_${index}`, so we can extract the index
      const rowKeyParts = rowKey.split('_');
      let rowId: string | undefined;
      
      if (rowKeyParts.length >= 2) {
        const lastPart = rowKeyParts[rowKeyParts.length - 1];
        const rowIndex = parseInt(lastPart, 10) - 1;
        if (!Number.isNaN(rowIndex) && rowIndex >= 0 && rowIndex < reference.rows.length) {
          const row = reference.rows[rowIndex];
          rowId = row?.id;
        }
      }
      
      // If we couldn't find by index, try to find by matching other cell values
      if (!rowId) {
        // Try to find row by matching a unique identifier (like the first cell)
        const firstColumn = refStructure.columns?.[0];
        if (firstColumn && rowData[firstColumn.id]) {
          const matchingRow = reference.rows.find((r: any) => {
            const cell = r.cells?.find((c: any) => c.id === firstColumn.id);
            return cell?.value === rowData[firstColumn.id];
          });
          if (matchingRow) {
            rowId = matchingRow.id;
          }
        }
      }
      
      if (!rowId) {
        logWarning(`Cannot find row ID for row key: ${rowKey}`, "ProjectExtract", undefined);
        return;
      }
      
      // Find column structure
      const columnStructure = findColumnInStructure(refStructure.columns || [], colDef.field);
      if (!columnStructure) {
        logWarning(`Column not found for field: ${colDef.field}`, "ProjectExtract", undefined);
        return;
      }
      
      const columnId = columnStructure.id;
      
      // Update the cell using updateReferenceCell
      const updatedExtract = updateReferenceCell(
        studyEntry.extract,
        referenceId,
        rowId,
        columnId,
        value,
        userId,
        userName
      );
      
      // Update the study entry
      studyEntry.extract = updatedExtract;
      
      // Save to localStorage
      const storageKey = `study_demo_${currentDatasetKey}_${studyExtractEntries.indexOf(studyEntry) + 1}`;
      saveTargetExtract('study', storageKey, updatedExtract);
      
      // Return the updated sourceInfo
      const updatedReference = updatedExtract.references?.find((r: any) => r.id === referenceId);
      const updatedRow = updatedReference?.rows?.find((r: any) => r.id === rowId);
      const updatedCell = updatedRow?.cells?.find((c: any) => c.id === columnId);
      return updatedCell?.sourceInfo || null;
    }
    
    // Handle topic cell changes
    // Returns the updated sourceInfo for the cell, or null if update failed
    function handleTopicCellChange(
      studyEntry: StudyExtractEntry,
      topicId: string,
      rowData: any,
      colDef: any,
      value: any,
      userId: string,
      userName: string
    ): any {
      const topicStructure = projectStructure?.topics?.find((t: any) => t.id === topicId);
      if (!topicStructure) {
        logWarning(`Topic structure not found: ${topicId}`, "ProjectExtract", undefined);
        return null;
      }
      
      // Parse row key to get grouper row ID
      // Format is `${studyId}_${topicId}_${grouperRowId}`
      const rowKey = rowData[STUDY_ROW_KEY_FIELD];
      if (!rowKey) {
        logWarning('Cannot update cell - missing row key', "ProjectExtract", undefined);
        return;
      }
      
      const rowKeyParts = rowKey.split('_');
      if (rowKeyParts.length < 3) {
        logWarning(`Invalid row key format: ${rowKey}`, "ProjectExtract", undefined);
        return;
      }
      
      // Extract grouper row ID (everything after `${studyId}_${topicId}_`)
      const grouperRowId = rowKeyParts.slice(2).join('_');
      
      // Find the topic in the study extract
      const topic = studyEntry.extract?.topics?.find((t: any) => t.id === topicId);
      if (!topic) {
        logWarning(`Topic not found: ${topicId}`, "ProjectExtract", undefined);
        return;
      }
      
      // Determine which table this column belongs to by checking the field prefix
      // Topic table columns have format `${tableId}__${columnId}` or `${tableId}__${columnId}_${fieldId}`
      const fieldName = colDef.field;
      let tableId: string | undefined;
      let columnId: string;
      
      // Check if field has table prefix (format: `tableId__columnId` or `tableId__columnId_fieldId`)
      if (fieldName.includes('__')) {
        const [tablePrefix, ...rest] = fieldName.split('__');
        tableId = tablePrefix;
        const remaining = rest.join('__');
        
        // Check if it's a grouped column (format: `columnId_fieldId`)
        if (remaining.includes('_')) {
          const [colId, fieldId] = remaining.split('_');
          // For grouped columns, we need to find the field within the column
          const tableStructure = topicStructure.tables?.find((t: any) => t.id === tableId);
          const column = tableStructure?.columns?.find((c: any) => c.id === colId);
          if (column?.type === 'group' && column.fields) {
            const field = column.fields.find((f: any) => f.id === fieldId);
            if (field) {
              columnId = field.id;
            } else {
              columnId = remaining;
            }
          } else {
            columnId = remaining;
          }
        } else {
          columnId = remaining;
        }
      } else {
        // No table prefix - this shouldn't happen for topic tables, but handle it
        logWarning(`Field name doesn't have table prefix: ${fieldName}`, "ProjectExtract", undefined);
        return;
      }
      
      if (!tableId) {
        logWarning(`Cannot determine table ID from field: ${fieldName}`, "ProjectExtract", undefined);
        return;
      }
      
      // Find the table row that matches this grouper row
      const table = topic.tables?.find((t: any) => t.id === tableId);
      if (!table || !Array.isArray(table.rows)) {
        logWarning(`Table not found or has no rows: ${tableId}`, "ProjectExtract", undefined);
        return;
      }
      
      // Find the row that matches the grouper row ID
      // Table rows have id set to the grouper row ID after normalization
      const tableRow = table.rows.find((r: any) => {
        const rowId = r.id || r.grouperId || r.grouper_row_id;
        return String(rowId) === String(grouperRowId);
      });
      
      if (!tableRow) {
        logWarning(`Table row not found for grouper row ID: ${grouperRowId}`, "ProjectExtract", undefined);
        return;
      }
      
      const tableRowId = tableRow.id || tableRow.grouperId || tableRow.grouper_row_id;
      
      // Update the cell using updateTargetCell
      const updatedExtract = updateTargetCell(
        studyEntry.extract,
        topicId,
        tableId,
        String(tableRowId),
        columnId,
        value,
        userId,
        userName
      );
      
      // Update the study entry
      studyEntry.extract = updatedExtract;
      
      // Save to localStorage
      const storageKey = `study_demo_${currentDatasetKey}_${studyExtractEntries.indexOf(studyEntry) + 1}`;
      saveTargetExtract('study', storageKey, updatedExtract);
      
      // Return the updated sourceInfo
      const updatedTopic = updatedExtract.topics?.find((t: any) => t.id === topicId);
      const updatedTable = updatedTopic?.tables?.find((t: any) => t.id === tableId);
      const updatedRow = updatedTable?.rows?.find((r: any) => {
        const rowId = r.id || r.grouperId || r.grouper_row_id;
        return String(rowId) === String(grouperRowId);
      });
      const updatedCell = updatedRow?.cells?.find((c: any) => c.id === columnId);
      return updatedCell?.sourceInfo || null;
    }
    
    // Helper function to find field in structure (handles nested fields)
    function findFieldInStructure(fields: any[], fieldId: string): any {
      for (const field of fields) {
        if (field.id === fieldId) {
          return field;
        }
        if (field.type === 'group' && Array.isArray(field.fields)) {
          // Check nested fields (format: `fieldId.nestedFieldId`)
          if (fieldId.startsWith(`${field.id}.`)) {
            const nestedFieldId = fieldId.substring(field.id.length + 1);
            const nestedField = findFieldInStructure(field.fields, nestedFieldId);
            if (nestedField) {
              return nestedField;
            }
          }
          // Also check direct nested field match
          const nestedField = findFieldInStructure(field.fields, fieldId);
          if (nestedField) {
            return nestedField;
          }
        }
      }
      return null;
    }
    
    // Helper function to find column in structure (handles grouped columns)
    function findColumnInStructure(columns: any[], fieldId: string): any {
      for (const column of columns) {
        if (column.id === fieldId) {
          return column;
        }
        if (column.type === 'group' && Array.isArray(column.fields)) {
          const field = column.fields.find((f: any) => f.id === fieldId);
          if (field) {
            return field;
          }
        }
      }
      return null;
    }
    
    function initializeGrid(columnDefs: any[], rowData: any[]) {
      if (!gridDiv) {
        // Grid div not available yet, try again after a short delay
        setTimeout(() => initializeGrid(columnDefs, rowData), 100);
        return;
      }
      
      if (columnDefs.length === 0) {
        // No columns to display
        return;
      }
      
      // Clean up existing grid
      destroyGrid();
      
      // Calculate row height based on content for multiline cells
      // Since defaultColDef has wrapText: true, all columns can have multiline content
      const getRowHeight = (params: any) => {
        if (!params.data) return 32; // Base row height if no data
        
        let maxHeight = 32; // Base row height
        
        // Helper function to check if a column/field has multiline content
        const checkColumnHeight = (colDef: any, fieldName: string, data: any) => {
          if (!colDef || !data || !fieldName) return 0;
          
          const value = data[fieldName];
          if (!value || value === '-' || typeof value !== 'string') return 0;
          
          // Check if column has wrapText enabled (explicitly or via defaultColDef)
          const hasWrapText = colDef.wrapText !== false; // Default is true from defaultColDef
          
          // Check cellStyle for multiline indicators
          const cellStyle = colDef.cellStyle || {};
          const isMultilineStyle = Boolean(
            cellStyle['white-space'] === 'normal' ||
            cellStyle['word-wrap'] === 'break-word' ||
            cellStyle['overflow-wrap'] === 'break-word'
          );
          
          // If column has wrapText or multiline style, calculate height
          if (hasWrapText || isMultilineStyle) {
            const columnWidth = colDef.width || colDef.minWidth || 200;
            const charsPerLine = Math.floor(columnWidth / 8); // Approximate chars per line
            const lines = Math.max(1, Math.ceil(value.length / charsPerLine));
            const estimatedHeight = 16 + (lines * 19); // Padding + line height
            return estimatedHeight;
          }
          
          return 0;
        };
        
        // Check each column for multiline content
        columnDefs.forEach((colDef: any) => {
          // Skip if column doesn't have a field (e.g., grouped column header)
          if (!colDef.field && !colDef.children) return;
          
          // For regular columns with a field
          if (colDef.field && params.data) {
            const height = checkColumnHeight(colDef, colDef.field, params.data);
            maxHeight = Math.max(maxHeight, height);
          }
          
          // For grouped columns, check children fields
          if (colDef.children && Array.isArray(colDef.children) && params.data) {
            colDef.children.forEach((child: any) => {
              if (child.field) {
                const height = checkColumnHeight(child, child.field, params.data);
                maxHeight = Math.max(maxHeight, height);
              }
            });
          }
        });
        
        // Cap at reasonable maximum to prevent excessive height
        return Math.min(maxHeight, 500);
      };
      
      // Check if current section is a topic to enable new filter sidebar
      const currentSection = mainSections.find((s) => s.id === activeTab);
      const isTopicGrid = currentSection?.type === 'topic';
      
      // Create a copy of columnDefs to avoid modifying the original
      let processedColumnDefs = columnDefs;
      
      // Enable filters on columns for topic grids
      if (isTopicGrid) {
        processedColumnDefs = columnDefs.map((colDef: any) => {
          // Enable filter on leaf columns (columns with fields, not group headers)
          // Use selectable filter to allow switching filter types in the new filter sidebar
          if (colDef.field && !colDef.children) {
            return {
              ...colDef,
              filter: 'agSelectableColumnFilter'
            };
          }
          // For grouped columns, recursively enable filters on children
          if (colDef.children && Array.isArray(colDef.children)) {
            return {
              ...colDef,
              children: colDef.children.map((child: any) => {
                if (child.field) {
                  return {
                    ...child,
                    filter: 'agSelectableColumnFilter'
                  };
                }
                return child;
              })
            };
          }
          return colDef;
        });
      }
      
      const gridOptions: any = {
        ...defaultGridOptions,
        columnDefs: processedColumnDefs,
        rowData: rowData,
        loading: false,
        getRowHeight: getRowHeight, // Calculate row height based on content
        // External filter for study filtering
        isExternalFilterPresent: () => isExternalFilterPresent(),
        doesExternalFilterPass: (node: any) => doesExternalFilterPass(node),
        // Apply quick filter if it exists (persistent across tabs)
        quickFilterText: quickFilterText || '',
        // Show "No data" overlay when grid is empty
        overlayNoRowsTemplate: '<div class="ag-overlay-no-rows"><span>No data</span></div>',
        // Enable sidebar with columns tool panel for all grids, and filters for topic grids
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                suppressPivotMode: true, // Disable pivot mode
                suppressRowGroups: true, // Disable row groups section
                suppressValues: true, // Disable values (aggregations) section
                suppressPivots: true, // Disable column labels (pivot) section
                suppressColumnFilter: true, // Disable column filter (we have separate filters panel)
                suppressColumnSelectAll: false, // Keep select all checkbox
                suppressColumnExpandAll: false, // Keep expand/collapse all
                suppressSyncLayoutWithGrid: false, // Keep columns in sync with grid
                suppressColumnMove: false // Allow reordering columns in tool panel
              }
            },
            ...(isTopicGrid ? [{
              id: 'filters-new',
              labelDefault: 'Filters',
              labelKey: 'filters',
              iconKey: 'filter',
              toolPanel: 'agNewFiltersToolPanel'
            }] : [])
          ],
          hiddenByDefault: false, // Sidebar visible
          defaultToolPanel: undefined // No panel open by default (sidebar will be collapsed showing just icons)
        },
        ...(isTopicGrid ? {
          enableFilterHandlers: true
        } : {}),
        onGridReady: (params: any) => {
          gridApi = params.api;
          
          // Store grid API for current tab to enable multi-sheet export
          if (activeTab) {
            gridApisByTab.set(activeTab, params.api);
          }
          
          // Apply filters after grid is ready (persistent across tabs)
          // Apply external filter (study filter) - always call onFilterChanged
          // The grid will check isExternalFilterPresent() internally
          params.api.onFilterChanged();
          
          // Apply quick filter if it exists (persistent across tabs)
          if (quickFilterText) {
            params.api.setGridOption('quickFilterText', quickFilterText);
          }
          
          // Show/hide "No data" overlay based on row count
          if (params.api.getDisplayedRowCount() === 0) {
            params.api.showNoRowsOverlay();
          } else {
            params.api.hideOverlay();
          }
        },
        onCellValueChanged: (params: any) => {
          handleCellValueChanged(params);
        },
        onFilterChanged: (params: any) => {
          // Show/hide "No data" overlay after filtering
          if (params.api.getDisplayedRowCount() === 0) {
            params.api.showNoRowsOverlay();
          } else {
            params.api.hideOverlay();
          }
        }
      };
      
      gridInstance = createGrid(gridDiv, gridOptions);
    }
  
    // Load reference data
    function loadReferenceData(referenceId: string) {
      const refStructure = projectStructure?.references?.find((r: any) => r.id === referenceId);
      
      if (!refStructure) {
        currentData = null;
        currentStructure = null;
        initializeGrid([], []);
        return;
      }

      currentStructure = refStructure;
      currentData = null;

      // Handle object-type references (like "study")
      if (refStructure.type === 'object') {
        const columnDefs: any[] = [];
        columnDefs.push(createStudyColumnDef("Study"));
        const rowData: any[] = [];

        // Process fields to create column definitions and row data
        const processFields = (fields: any[], parentPath: string = '') => {
          fields.forEach((field: any) => {
            if (field.type === 'group' && Array.isArray(field.fields)) {
              // Handle grouped fields - create a parent column with children
              const groupColumns = field.fields.map((subField: any) => {
                const fieldPath = parentPath ? `${parentPath}.${field.id}.${subField.id}` : `${field.id}.${subField.id}`;
                return {
                  headerName: subField.label || subField.id,
                  field: fieldPath,
                  colId: fieldPath,
                  pinned: undefined,
                  width: subField.multiline ? 200 : undefined,
                  minWidth: subField.multiline ? 200 : undefined,
                  maxWidth: subField.multiline ? 200 : undefined,
                  suppressSizeToFit: subField.multiline,
                  suppressAutoSize: subField.multiline,
                  resizable: false,
                  sortable: false,
                  filter: false,
                  lockPosition: false,
                  suppressHeaderMenuButton: true,
                  suppressHeaderContextMenu: true,
                  wrapText: true,
                  autoHeight: true,
                  cellStyle: {
                    'white-space': 'normal',
                    'word-wrap': 'break-word',
                    'overflow-wrap': 'break-word',
                    'padding': '8px 8px',
                    'line-height': 'normal',
                    'vertical-align': 'top'
                  },
                  cellRenderer: (params: any) => {
                    const value = params.value || "-";
                    const sourceInfo = params.data?.[`${fieldPath}_sourceInfo`];
                    return showIndicators
                      ? createCellWithIndicator(value, sourceInfo, DEFAULT_DOCUMENT_ID)
                      : value;
                  }
                };
              });

              if (groupColumns.length > 0) {
                columnDefs.push({
                  headerName: field.label || field.id,
                  marryChildren: true,
                  children: groupColumns,
                  resizable: false,
                  lockPosition: false,
                  suppressHeaderMenuButton: true,
                  suppressHeaderContextMenu: true,
                });
              }
            } else {
              // Handle regular fields
              const fieldPath = parentPath ? `${parentPath}.${field.id}` : field.id;
              columnDefs.push({
                headerName: field.label || field.id,
                field: fieldPath,
                colId: fieldPath,
                pinned: undefined,
                width: field.multiline ? 200 : undefined,
                minWidth: field.multiline ? 200 : undefined,
                maxWidth: field.multiline ? 200 : undefined,
                suppressSizeToFit: field.multiline,
                suppressAutoSize: field.multiline,
                resizable: false,
                sortable: false,
                filter: false,
                lockPosition: false,
                suppressHeaderMenuButton: true,
                suppressHeaderContextMenu: true,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                  'white-space': 'normal',
                  'word-wrap': 'break-word',
                  'overflow-wrap': 'break-word',
                  'padding': '8px 8px',
                  'line-height': 'normal',
                  'vertical-align': 'top'
                },
                cellRenderer: (params: any) => {
                  const value = params.value || "-";
                  const sourceInfo = params.data?.[`${fieldPath}_sourceInfo`];
                  return showIndicators
                    ? createCellWithIndicator(value, sourceInfo, DEFAULT_DOCUMENT_ID)
                    : value;
                }
              });
            }
          });
        };

        if (refStructure.fields && Array.isArray(refStructure.fields)) {
          processFields(refStructure.fields);
        }

        studyExtractEntries.forEach((entry) => {
          const reference = entry.extract?.references?.find((ref: any) => ref.id === referenceId);
          if (!reference) {
            return;
          }
          
          // For object-type references, create one row per study
          // The reference object contains values directly (not rows array)
          const rowDataItem: any = {
            [STUDY_COLUMN_FIELD]: entry.name,
            [STUDY_ID_FIELD]: entry.id,
            [STUDY_ROW_KEY_FIELD]: `${entry.id}_${referenceId}`,
          };

          // Process fields from the reference object
          if (refStructure.fields && Array.isArray(refStructure.fields)) {
            refStructure.fields.forEach((field: any) => {
              if (field.type === 'group' && Array.isArray(field.fields)) {
                // Handle grouped fields
                field.fields.forEach((subField: any) => {
                  const fieldPath = `${field.id}.${subField.id}`;
                  const value = reference.values?.find((v: any) => v.id === subField.id);
                  rowDataItem[fieldPath] = value?.value || '-';
                  if (value?.sourceInfo) {
                    rowDataItem[`${fieldPath}_sourceInfo`] = value.sourceInfo;
                  }
                });
              } else {
                // Handle regular fields
                const value = reference.values?.find((v: any) => v.id === field.id);
                rowDataItem[field.id] = value?.value || '-';
                if (value?.sourceInfo) {
                  rowDataItem[`${field.id}_sourceInfo`] = value.sourceInfo;
                }
              }
            });
          }

          rowData.push(rowDataItem);
        });

        initializeGrid(columnDefs, rowData);
        return;
      }

      // Handle table-type references
      if (refStructure.type !== 'table') {
        currentData = null;
        currentStructure = refStructure;
        initializeGrid([], []);
        return;
      }

      const columnDefs = createReferenceColumnDefs(refStructure.columns || []);
      const rowData: any[] = [];

      studyExtractEntries.forEach((entry) => {
        const reference = entry.extract?.references?.find((ref: any) => ref.id === referenceId);
        if (!reference || !Array.isArray(reference.rows)) {
          return;
        }
        const studyRows = createReferenceRowData(reference.rows, entry);
        rowData.push(...studyRows);
      });

      initializeGrid(columnDefs, rowData);
    }
  
    // Load topic data
    function loadTopicData(topicId: string) {
      const structureTopic = projectStructure?.topics?.find((st: any) => st.id === topicId);
      
      if (!structureTopic) {
        currentData = null;
        currentStructure = null;
        initializeGrid([], []);
        return;
      }
      
      currentStructure = structureTopic;
      currentData = null;
      
      // Use the combined table approach for all topics (same as baseline)
      createCombinedTopicTable(structureTopic, topicId);
    }

    // Create combined topic table with Study, Group, and all tables concatenated
    function createCombinedTopicTable(structureTopic: any, topicId: string) {
      const columnDefs: any[] = [];
      columnDefs.push(createStudyColumnDef("Study"));

      const hasGrouper = Boolean(structureTopic.grouper);
      const grouperLevels = Array.isArray(structureTopic.grouper?.levels) 
        ? structureTopic.grouper.levels 
        : [];
      const isMultiLevel = grouperLevels.length > 1;
      
      // Add grouper columns - one per level for multi-level groupers
      if (hasGrouper) {
        if (isMultiLevel) {
          // Multi-level grouper: create one column per level
          grouperLevels.forEach((level: any, levelIndex: number) => {
            const levelNumber = String(level.level ?? level.number ?? levelIndex + 1);
            const levelLabel = level.label || `Level ${levelNumber}`;
            const fieldName = `group_level_${levelNumber}`;
            
            columnDefs.push({
              headerName: levelLabel,
              marryChildren: true,
              children: [
                {
                  field: fieldName,
                  headerName: levelLabel,
                  pinned: 'left',
                  width: 200,
                  minWidth: 200,
                  maxWidth: 280,
                  suppressSizeToFit: true,
                  suppressAutoSize: true,
                  resizable: false,
                  sortable: false,
                  filter: false,
                  lockPosition: true,
                  suppressHeaderMenuButton: true,
                  suppressHeaderContextMenu: true,
                  cellStyle: {
                    'white-space': 'normal',
                    'word-wrap': 'break-word',
                    'overflow-wrap': 'break-word',
                    'padding': '8px 8px',
                    'font-weight': 'bold'
                  },
                  cellRenderer: (params: any) => {
                    const value = params.value || "-";
                    const sourceInfo = params.data?.[`${fieldName}_sourceInfo`];
                    return showIndicators
                      ? createCellWithIndicator(value, sourceInfo, DEFAULT_DOCUMENT_ID)
                      : value;
                  }
                }
              ]
            });
          });
        } else {
          // Single-level grouper: create one Group column
          columnDefs.push({
            headerName: structureTopic.grouper?.label || "Group",
            marryChildren: true,
            children: [
              {
                field: 'group',
                headerName: 'Group',
                pinned: 'left',
                width: 200,
                minWidth: 200,
                maxWidth: 280,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                sortable: false,
                filter: false,
                lockPosition: true,
                suppressHeaderMenuButton: true,
                suppressHeaderContextMenu: true,
                cellStyle: {
                  'white-space': 'normal',
                  'word-wrap': 'break-word',
                  'overflow-wrap': 'break-word',
                  'padding': '8px 8px',
                  'font-weight': 'bold'
                },
                cellRenderer: (params: any) => {
                  const value = params.value || "-";
                  const sourceInfo = params.data?.['group_sourceInfo'];
                  return showIndicators
                    ? createCellWithIndicator(value, sourceInfo, DEFAULT_DOCUMENT_ID)
                    : value;
                }
              }
            ]
          });
        }
      }

      // Add root-level grouper columns (e.g., sample_size, analysis_set, etc.) after Group columns
      // These should scroll horizontally with table columns, not be pinned
      const rootGrouperColumns = projectStructure?.grouper?.columns || [];
      if (rootGrouperColumns.length > 0 && hasGrouper) {
        rootGrouperColumns.forEach((columnDef: any) => {
          const lookupRenderer = getLookupCellRenderer(columnDef, projectStructure);
          const isMultiline = columnDef.multiline === true;
          columnDefs.push({
            field: columnDef.id,
            headerName: (columnDef.label || columnDef.id).toUpperCase(),
            minWidth: isMultiline ? 200 : 100,
            width: isMultiline ? 200 : undefined, // Fixed width for multiline, auto-size for others
            maxWidth: isMultiline ? 200 : undefined,
            suppressSizeToFit: isMultiline, // Prevent auto-sizing for multiline columns
            suppressAutoSize: isMultiline,
            editable: false,
            wrapText: isMultiline, // Enable text wrapping only for multiline columns
            autoHeight: false, // Disable autoHeight - use getRowHeight instead
            cellRenderer: (params: any) => {
              let displayValue = params.value || '-';
              if (lookupRenderer && displayValue !== '-') {
                displayValue = lookupRenderer(params) || '-';
              }
              const sourceInfo = params.data?.[`${columnDef.id}_sourceInfo`];
              return showIndicators
                ? createCellWithIndicator(displayValue, sourceInfo, DEFAULT_DOCUMENT_ID)
                : displayValue;
            },
            sortable: false,
            filter: false,
            resizable: false,
            lockPosition: false, // Allow horizontal scrolling
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            cellStyle: isMultiline ? {
              'white-space': 'normal',
              'word-wrap': 'break-word',
              'overflow-wrap': 'break-word',
              'padding': '8px 8px',
              'line-height': 'normal',
              'vertical-align': 'top'
            } : {
              'padding': '8px 8px'
            }
          });
        });
      }

      // Add all table columns concatenated horizontally
      const tableStructures = Array.isArray(structureTopic.tables) ? structureTopic.tables : [];
      tableStructures.forEach((tableStructure: any) => {
        // Create column definitions for this table
        // Don't pass grouperStructure - we want table columns, not grouper columns
        const tableColumns = createTopicColumnDefs(
          tableStructure.columns || [],
          undefined, // Don't pass grouper structure for data tables
          false,
          { fieldPrefix: `${tableStructure.id}__` }
        ).filter((columnDef: any) => {
          // Filter out grouper_name columns since we have a dedicated Group column
          if (typeof columnDef.field === 'string' && columnDef.field.endsWith('__grouper_name')) {
            return false;
          }
          return true;
        });

        // Add table header with its columns as children
        if (tableColumns.length > 0) {
          columnDefs.push({
            headerName: tableStructure.label || toTitleCase(tableStructure.id),
            marryChildren: true,
            children: tableColumns,
            resizable: false,
            lockPosition: false,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
          });
        }
      });

      const rowData: any[] = [];
      
      // rootGrouperColumns is already defined above for column definitions, reuse it here

      // Process each study
      studyExtractEntries.forEach((entry) => {
        const topic = entry.extract?.topics?.find((t: any) => t.id === topicId);
        if (!topic) {
          return;
        }
        const studyReference = entry.extract?.references?.find((ref: any) => ref.id === "study");
        const studyIdValue = studyReference?.values?.find?.((value: any) => value.id === "studyid");
        const studySourceInfo = studyIdValue?.sourceInfo;

        // Get grouper rows - handle both array format and object with rows property
        let grouperRows: any[] = [];
        if (hasGrouper && topic.grouper) {
          if (Array.isArray(topic.grouper)) {
            // Normalized format: grouper is an array
            try {
              grouperRows = ensureGrouperArrayLocal(topic.grouper, `topic ${topicId}`);
            } catch (error) {
              logError(error, "ProjectExtract", undefined, { message: `Failed to process grouper array for topic ${topicId}` });
              grouperRows = [null];
            }
          } else if (topic.grouper && Array.isArray(topic.grouper.rows)) {
            // Legacy format: grouper is an object with rows property
            grouperRows = topic.grouper.rows;
          } else {
            grouperRows = [null];
          }
        } else {
          grouperRows = [null];
        }
        
        // Pre-process table data - store as Maps keyed by rowId for each table
        // Table rows are matched to grouper rows by rowId, not by index
        const tableRowDataByTable = new Map<string, Map<string, any>>();

        tableStructures.forEach((tableStructure: any) => {
          const table = Array.isArray(topic.tables)
            ? topic.tables.find((t: any) => t.id === tableStructure.id)
            : null;

          const tableRows = table?.rows || [];
          
          // Process row data - this creates the field names with the table prefix
          // Pass grouperRows (normalized array) instead of topic.grouper for consistency
          const tableRowData = createTopicRowData(
            tableRows,
            tableStructure.columns || [],
            grouperRows.length > 0 ? grouperRows : undefined,
            topic.tables,
            false,
            structureTopic.grouper,
            { fieldPrefix: `${tableStructure.id}__`, studyContext: entry.extract }
          );

          // Store row data in a Map keyed by rowId for efficient lookup
          // After normalization, table rows have id set from their original rowId
          // Grouper rows have rowId, so we match grouper rowId with table id
          const rowMap = new Map<string, any>();
          tableRowData.forEach((row: any, idx: number) => {
            // The id field in processed row data comes from the original rowId after normalization
            // If id is not set, try to get it from the original table row
            const originalRow = tableRows[idx];
            const rowId = row.id || originalRow?.id || originalRow?.grouperId;
            
            if (rowId) {
              // Remove id from the row data (we use it as the key, not as data)
              const { id, ...rest } = row;
              // Store by rowId for matching with grouper rows (normalize to string)
              const rowIdKey = String(rowId).trim();
              rowMap.set(rowIdKey, rest);
            }
          });
          
          tableRowDataByTable.set(tableStructure.id, rowMap);
        });

        // Sort grouper rows by order number before processing
        const sortedGrouperRows = grouperRows.map((row, index) => ({ row, originalIndex: index }))
          .sort((a, b) => {
            const aOrder = getNumericOrderValue(a.row, a.originalIndex + 1);
            const bOrder = getNumericOrderValue(b.row, b.originalIndex + 1);
            return aOrder - bOrder;
          })
          .map(item => item.row);
        
        // Create one row per grouper row
        sortedGrouperRows.forEach((grouperRow: any, rowIndex: number) => {
          const rowKeyBase = grouperRow?.grouperId || grouperRow?.id 
            ? String(grouperRow.grouperId || grouperRow.id) 
            : `row_${rowIndex}`;
          
          // Get row number for sorting
          const orderNumber = getNumericOrderValue(grouperRow, rowIndex + 1);
          
          const rowDataItem: any = {
            [STUDY_COLUMN_FIELD]: entry.name,
            [STUDY_ID_FIELD]: entry.id,
            [STUDY_ROW_KEY_FIELD]: `${entry.id}_${topicId}_${rowKeyBase}`,
            [STUDY_SOURCE_FIELD]: studySourceInfo,
            _grouperOrderNumber: orderNumber
          };

          // Set group name(s) if grouper exists
          if (hasGrouper) {
            if (isMultiLevel) {
              // Multi-level: populate each level column
              grouperLevels.forEach((level: any, levelIndex: number) => {
                const levelNumber = String(level.level ?? level.number ?? levelIndex + 1);
                const fieldName = `group_level_${levelNumber}`;
                
                // Get refs from grouper row for this level
                const refs = getLevelRefsLocal(grouperRow, levelNumber);
                
                // Get reference definitions from level structure (can be refs or sources)
                const levelRefs = level.refs || [];
                const levelSources = (level.sources || []).filter((s: any) => s?.type === 'reference');
                
                // Use refs if available, otherwise use sources
                const refDefs = levelRefs.length > 0 ? levelRefs : levelSources;
                
                const displayParts: string[] = [];
                
                refDefs.forEach((refDef: any, refIndex: number) => {
                  const refId = refs[refIndex];
                  if (!refId) return;
                  
                  // Handle both refs format ({ refId, columnId }) and sources format ({ refTable, refId })
                  // In refs format, refId is the reference table name, and we look up the value by the refId from grouper row
                  // In sources format, refTable is the reference table name
                  const refTable = refDef.refTable || refDef.refId;
                  
                  if (refTable) {
                    // refId from grouper row is the ID to look up in the reference table
                    const refValue = getReferenceValue(refTable, refId, entry.extract);
                    if (refValue && refValue !== '-') {
                      displayParts.push(refValue);
                    }
                  }
                });
                
                let displayValue = displayParts.length > 0 ? displayParts.join(' ● ') : '-';
                
                // If still empty, check for custom value (only for first level)
                if ((!displayValue || displayValue === '-') && levelNumber === '1') {
                  const customValue = getCustomValue(grouperRow);
                  if (customValue) {
                    displayValue = customValue;
                  } else {
                    // No references and no custom value - show "Study" (same as single-level)
                    displayValue = 'Study';
                  }
                }
                
                // For first level, if displayValue matches study name or rowId, show "Study" instead
                if (levelNumber === '1' && displayValue && displayValue !== '-') {
                  const rowId = grouperRow?.grouperId || grouperRow?.id;
                  const rowIdStr = rowId ? String(rowId) : '';
                  if (displayValue === entry.name || displayValue === rowIdStr) {
                    // Check for custom value first
                    const customValue = getCustomValue(grouperRow);
                    if (customValue) {
                      displayValue = customValue;
                    } else {
                      displayValue = 'Study';
                    }
                  }
                }
                
                rowDataItem[fieldName] = displayValue || '-';
                
                // Get source info for this level if available
                if (grouperRow && Array.isArray(grouperRow.levels)) {
                  const levelData = grouperRow.levels.find(
                    (lvl: any) => String(lvl.level ?? lvl.number ?? '') === levelNumber
                  );
                  if (levelData) {
                    // Try to find sourceInfo from any cell in this level
                    const cells = levelData.cells || [];
                    const firstCell = cells[0];
                    if (firstCell?.sourceInfo) {
                      rowDataItem[`${fieldName}_sourceInfo`] = firstCell.sourceInfo;
                    }
                  }
                }
              });
            } else {
              // Single-level: use group field
              let groupName = getGroupNameFromGrouperRow(grouperRow, entry.extract, structureTopic.grouper);
              const rowId = grouperRow?.grouperId || grouperRow?.id;
              const rowIdStr = rowId ? String(rowId) : '';
              
              // getDisplayName should handle custom values and study fallback correctly
              // But we need to check if it returned the study name or rowId as fallback
              
              // If group name is empty or invalid, check for custom value first, then show "Study"
              if (!groupName || groupName === '-') {
                const customValue = getCustomValue(grouperRow);
                if (customValue) {
                  groupName = customValue;
                } else {
                  // No references and no custom value - show "Study" (different from ExtractItems2.svelte)
                  groupName = 'Study';
                }
              } 
              // If group name matches the study name (fallback from getDisplayName), show "Study" instead
              // This is different from ExtractItems2.svelte which shows the study name
              else if (groupName === entry.name) {
                groupName = 'Study';
              }
              // If group name matches the rowId (fallback from getDisplayName when study name not found),
              // check for custom value first, then show "Study"
              else if (groupName === rowIdStr && rowIdStr) {
                const customValue = getCustomValue(grouperRow);
                if (customValue) {
                  groupName = customValue;
                } else {
                  // No references and no custom value - show "Study"
                  groupName = 'Study';
                }
              }
              // Otherwise, use the group name as-is (should handle custom values correctly)
              
              rowDataItem.group = groupName || '-';
              
              // Get source info for group if available
              if (grouperRow) {
                if (Array.isArray(grouperRow.levels) && grouperRow.levels.length > 0) {
                  const firstLevel = grouperRow.levels[0];
                  const nameCell = firstLevel?.cells?.find((c: any) => c.id === 'name');
                  if (nameCell?.sourceInfo) {
                    rowDataItem.group_sourceInfo = nameCell.sourceInfo;
                  }
                } else if (Array.isArray(grouperRow.cells)) {
                  const nameCell = grouperRow.cells.find((c: any) => c.id === 'name');
                  if (nameCell?.sourceInfo) {
                    rowDataItem.group_sourceInfo = nameCell.sourceInfo;
                  }
                }
              }
            }
          }

          // Add root-level grouper column values (e.g., sample_size, analysis_set, etc.)
          // Read grouper cell values from cells array
          const grouperCells = Array.isArray(grouperRow?.cells) ? grouperRow.cells : [];
          rootGrouperColumns.forEach((columnDef: any) => {
            const cell = grouperCells.find((c: any) => c.id === columnDef.id);
            const cellValue = cell?.value;
            // Only set to '-' if value is explicitly empty/null/undefined, otherwise use the actual value
            const finalValue = (cellValue === null || cellValue === undefined || cellValue === '') ? '-' : cellValue;
            rowDataItem[columnDef.id] = finalValue;
            // Store sourceInfo if available
            if (cell?.sourceInfo) {
              rowDataItem[`${columnDef.id}_sourceInfo`] = cell.sourceInfo;
            }
          });

          // Add all table data for this grouper row
          // Match table rows to grouper rows by rowId
          tableStructures.forEach((tableStructure: any) => {
            const rowMap = tableRowDataByTable.get(tableStructure.id);
            if (!rowMap || rowMap.size === 0) {
              // If no table rows, skip this table
              return;
            }
            
            // Match table row to grouper row by rowId
            // Grouper rows have rowId, table rows have id (from rowId after normalization)
            // So we match grouper rowId with table id (stored as key in the map)
            // Normalize the rowId key to ensure consistent matching
            const normalizedRowKey = String(rowKeyBase).trim();
            let tableRow = rowMap.get(normalizedRowKey);
            
            // If direct match fails, try alternative formats (case-insensitive, numeric comparison)
            if (!tableRow) {
              // Try case-insensitive match
              for (const [key, value] of rowMap.entries()) {
                if (String(key).trim().toLowerCase() === normalizedRowKey.toLowerCase()) {
                  tableRow = value;
                  break;
                }
              }
            }
            
            // Add table row data to the row item
            if (tableRow) {
              Object.entries(tableRow).forEach(([fieldName, value]) => {
                // Skip id, __studySourceInfo, and grouper_name fields
                if (fieldName === 'id' || 
                    fieldName === STUDY_SOURCE_FIELD || 
                    fieldName.endsWith('__grouper_name')) {
                  return;
                }
                // Add the field (table data takes precedence for table columns)
                rowDataItem[fieldName] = value;
              });
            }
          });

          rowData.push(rowDataItem);
        });
      });

      // For now, just create empty row data to display columns
      // We'll add data matching logic next
      if (rowData.length === 0) {
        // Create a single empty row with all fields set to '-' to display columns
        const emptyRow: any = {
          [STUDY_COLUMN_FIELD]: '-',
          [STUDY_ID_FIELD]: '-',
          [STUDY_ROW_KEY_FIELD]: `${topicId}_empty_row`,
        };
        
        if (hasGrouper) {
          emptyRow.group = '-';
          
          // Add empty values for root-level grouper columns
          rootGrouperColumns.forEach((columnDef: any) => {
            emptyRow[columnDef.id] = '-';
          });
        }
        
        // Add empty values for all table columns so they're visible
        tableStructures.forEach((tableStructure: any) => {
          (tableStructure.columns || []).forEach((column: any) => {
            if (column.type === 'group' && column.fields) {
              // For grouped columns, add empty values for each field
              column.fields.forEach((field: any) => {
                emptyRow[`${tableStructure.id}__${column.id}_${field.id}`] = '-';
              });
            } else {
              // For regular columns, add empty value
              emptyRow[`${tableStructure.id}__${column.id}`] = '-';
            }
          });
        });
        
        rowData.push(emptyRow);
      }

      // Sort rows by study, then by grouper row number within each study
      rowData.sort((a: any, b: any) => {
        // First sort by study ID
        const studyIdA = a[STUDY_ID_FIELD] || '';
        const studyIdB = b[STUDY_ID_FIELD] || '';
        if (studyIdA !== studyIdB) {
          return studyIdA.localeCompare(studyIdB);
        }
        
        // Then sort by study name (for same study ID, sort by name)
        const studyNameA = a[STUDY_COLUMN_FIELD] || '';
        const studyNameB = b[STUDY_COLUMN_FIELD] || '';
        if (studyNameA !== studyNameB) {
          return studyNameA.localeCompare(studyNameB);
        }
        
        // Finally sort by grouper row number within the same study
        if (hasGrouper) {
          const rowNumA = a._grouperOrderNumber ?? Number.MAX_SAFE_INTEGER;
          const rowNumB = b._grouperOrderNumber ?? Number.MAX_SAFE_INTEGER;
          if (rowNumA !== rowNumB) {
            return rowNumA - rowNumB;
          }
        }
        
        return 0;
      });

      initializeGrid(columnDefs, rowData);
    }
  
    // Create combined baseline table with Group column fixed and other columns from all tables
    function createCombinedBaselineTable(structureTopic: any, _legacyTopic?: any) {
      const columnDefs: any[] = [];
      columnDefs.push(createStudyColumnDef("Study"));

      columnDefs.push({
        headerName: structureTopic.grouper?.label || "Group",
        marryChildren: true,
        children: [
          {
            field: 'group',
            headerName: 'Group',
            pinned: 'left',
            width: 200,
            minWidth: 200,
            maxWidth: 220,
            suppressSizeToFit: true,
            suppressAutoSize: true,
            resizable: false,
            sortable: false,
            filter: false,
            lockPosition: true,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            cellStyle: {
              'white-space': 'normal',
              'word-wrap': 'break-word',
              'overflow-wrap': 'break-word',
              'padding': '8px 8px',
              'font-weight': 'bold'
            }
          }
        ]
      });

      const tableStructures = Array.isArray(structureTopic.tables) ? structureTopic.tables : [];
      tableStructures.forEach((tableStructure: any) => {
        const tableColumns = createTopicColumnDefs(
          tableStructure.columns || [],
          structureTopic.grouper,
          false,
          { fieldPrefix: `${tableStructure.id}__` }
        ).filter((columnDef: any) => {
          if (typeof columnDef.field === 'string' && columnDef.field.endsWith('__grouper_name')) {
            return false;
          }
          return true;
        });

        if (tableColumns.length > 0) {
          columnDefs.push({
            headerName: tableStructure.label || toTitleCase(tableStructure.id),
            marryChildren: true,
            children: tableColumns,
          });
        }
      });

      const rowData: any[] = [];

      studyExtractEntries.forEach((entry) => {
        const topic = entry.extract?.topics?.find((t: any) => t.id === 'baseline');
        if (!topic) {
          return;
        }
        const baselineStudyReference = entry.extract?.references?.find((ref: any) => ref.id === "study");
        const baselineStudyIdValue = baselineStudyReference?.values?.find?.((value: any) => value.id === "studyid");
        const baselineStudySourceInfo = baselineStudyIdValue?.sourceInfo;

        const grouperRows = Array.isArray(topic.grouper?.rows) ? topic.grouper.rows : [];
        const rowsToProcess = grouperRows.length > 0 ? grouperRows : [null];

        const tableRowDataCache = new Map<string, Map<string, any>>();

        tableStructures.forEach((tableStructure: any) => {
          const table = Array.isArray(topic.tables)
            ? topic.tables.find((t: any) => t.id === tableStructure.id)
            : null;

          const tableRowData = createTopicRowData(
            table?.rows || [],
            tableStructure.columns || [],
            topic.grouper,
            topic.tables,
            false,
            structureTopic.grouper,
            { fieldPrefix: `${tableStructure.id}__`, studyContext: entry.extract }
          );

          const rowMap = new Map<string, any>();
          tableRowData.forEach((row: any, rowIndex: number) => {
            const key = row.id ? String(row.id) : `${tableStructure.id}_${rowIndex + 1}`;
            const normalizedRow = { ...row };
            delete normalizedRow.id;
            rowMap.set(key, normalizedRow);
          });
          tableRowDataCache.set(tableStructure.id, rowMap);
        });

        rowsToProcess.forEach((grouperRow: any, rowIndex: number) => {
          const rowKeyBase = grouperRow?.id ? String(grouperRow.id) : `row_${rowIndex + 1}`;
          const rowDataItem: any = {
            [STUDY_COLUMN_FIELD]: entry.name,
            [STUDY_ID_FIELD]: entry.id,
            [STUDY_ROW_KEY_FIELD]: `${entry.id}_baseline_${rowKeyBase}`,
            group: '-',
            [STUDY_SOURCE_FIELD]: baselineStudySourceInfo,
          };

          if (grouperRow) {
            let groupName = '';
            if (Array.isArray(grouperRow.levels) && grouperRow.levels.length > 0) {
              const levelCells = grouperRow.levels[0]?.cells || [];
              const nameCell = levelCells.find((c: any) => c.id === 'name');
              if (nameCell) {
                if (nameCell.ref && nameCell.refID) {
                  groupName = getReferenceValue(nameCell.ref, nameCell.refID, entry.extract);
                } else {
                  groupName = nameCell.value || '';
                }
              }
            }
            if (!groupName && Array.isArray(grouperRow.cells)) {
              const directNameCell = grouperRow.cells.find((c: any) => c.id === 'name');
              if (directNameCell) {
                groupName = directNameCell.value || '';
              }
            }
            rowDataItem.group = groupName || '-';
          }

          tableStructures.forEach((tableStructure: any) => {
            const cache = tableRowDataCache.get(tableStructure.id);
            if (!cache) {
              return;
            }
            const tableRow = cache.get(rowKeyBase) ?? cache.get(String(rowIndex + 1));
            if (!tableRow) {
              return;
            }

            Object.entries(tableRow).forEach(([fieldName, value]) => {
              if (fieldName === 'id' || fieldName.endsWith('__grouper_name')) {
                return;
              }
              rowDataItem[fieldName] = value;
            });
          });

          rowData.push(rowDataItem);
        });
      });

      if (rowData.length === 0) {
        rowData.push({
          [STUDY_COLUMN_FIELD]: '-',
          [STUDY_ID_FIELD]: '-',
          [STUDY_ROW_KEY_FIELD]: 'baseline_empty_row',
          group: '-',
        });
      }

      initializeGrid(columnDefs, rowData);
    }
  
    // Handle tab change
    async function handleTabChange(tabId: string) {
      await ensureStoresLoaded();
  
      const section = mainSections.find((s) => s.id === tabId);
      if (!section) {
        return;
      }
  
      activeTab = tabId;
      hasLoadedInitialTab = true;
  
      await tick();
  
      if (!gridDiv) {
        return;
      }
  
      if (section.type === "reference") {
        loadReferenceData(section.sourceId);
      } else if (section.type === "topic") {
        loadTopicData(section.sourceId);
      }
    }
  
  
    // Handle cancel
    // Lifecycle
  onMount(() => {
    if (projectId) {
      ensureStoresLoaded().catch((error) => {
        logError(error, "ProjectExtract", undefined, { message: "GridSheetCustom failed to initialize stores on mount" });
      });
      ensureProjectDetailsLoaded().catch((error) => {
        logError(error, "ProjectExtract", undefined, { message: "ProjectExtract failed to initialize project details on mount" });
      });
    }
  });
  
  $: if (projectId) {
    ensureStoresLoaded().catch((error) => {
      logError(error, "ProjectExtract", undefined, { message: "GridSheetCustom failed to initialize stores for project change" });
    });
    ensureProjectDetailsLoaded().catch((error) => {
      logError(error, "ProjectExtract", undefined, { message: "ProjectExtract failed to refresh project details for project change" });
    });
  } else {
    projectDetails = null;
    currentProjectDetailsId = null;
  }
  
    onDestroy(() => {
      destroyGrid();
    });
  
    async function ensureInitialTab() {
      if (!show) {
        return;
      }
      await ensureStoresLoaded();
      if (mainSections.length === 0) {
        return;
      }
      if (hasLoadedInitialTab && mainSections.some((section) => section.id === activeTab)) {
        return;
      }
      const firstSection = mainSections[0];
      await handleTabChange(firstSection.id);
    }
  
    $: if (!show && (gridInstance || gridApi)) {
      destroyGrid();
      hasLoadedInitialTab = false;
      activeTab = "";
    }
  
    $: if (show && !storesInitialized) {
      ensureStoresLoaded().catch((error) => {
        logError(error, "ProjectExtract", undefined, { message: "GridSheetCustom failed to initialize stores when shown" });
      });
    }
  
  $: if (show && mainSections.length > 0) {
      ensureInitialTab();
    }

  $: projectTitle = (() => {
    const name = typeof projectDetails?.name === "string" ? projectDetails.name.trim() : "";
    if (name) {
      return name;
    }
    const title = typeof projectDetails?.title === "string" ? projectDetails.title.trim() : "";
    if (title) {
      return title;
    }
    return projectId || "Untitled Project";
  })();

  function openExternal() {
    if (projectId) {
      window.open(`/project-extract?id=${projectId}`, '_blank');
    }
  }
</script>

<div class="project-extract-wrapper">
  <header class="panel-header">
    <div class="panel-header-label">
      <span class="panel-header-label">Project Extract:</span>
      <span class="panel-header-title">{projectTitle}</span>
      {#if showExternalLink}
        <Button on:click={openExternal} pill={true} outline={true} class="general-button icon-only borderless" size="sm">
          <FontAwesomeIcon icon={faUpRightFromSquare} class="w-4 h-4" />
        </Button>
      {/if}
    </div>
  </header>

  <div class="project-extract-content">
    <Tabs
      tabStyle="underline"
      contentClass="p-0"
      class="project-extract-tabs"
    >
      {#each mainSections as section (section.id)}
        <TabItem
          class="project-extract-tab-item"
          open={activeTab === section.id}
          on:click={() => handleTabChange(section.id)}
        >
          <div slot="title" class="project-extract-tab-title">
            <FontAwesomeIcon icon={faTable} class="w-4 h-4" />
            {section.label}
          </div>
          <div class="tool-controls ml-4 mr-4 mt-2 mb-2">
            <div class="tool-group">
              <!-- Study Filter Dropdown -->
              <div class="tool-buttons" style="display: flex; align-items: center; gap: 0.5rem;">
                <span class="tool-toggle-label">Studies:</span>
                <div 
                  class="study-filter-dropdown-wrapper" 
                  style="position: relative;"
                  role="group"
                  aria-label="Study filter"
                  bind:this={dropdownWrapper}
                >
                  <Button size="sm" color="light" class="study-filter-button" on:click={(e) => { e.stopPropagation(); isDropdownOpen = !isDropdownOpen; }}>
                    {selectedStudies.size === studyExtractEntries.length 
                      ? 'All' 
                      : `${selectedStudies.size} of ${studyExtractEntries.length} selected`}
                    <FontAwesomeIcon icon={faChevronDown} class="w-3 h-3 ml-1" />
                  </Button>
                  {#if isDropdownOpen}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div 
                      class="study-filter-dropdown-content"
                      data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
                      use:clickOutside={() => isDropdownOpen = false}
                      on:click|stopPropagation
                      role="dialog"
                      aria-label="Study filter dropdown"
                    >
                      <div class="study-filter-search">
                        <input 
                          type="text" 
                          placeholder="Search studies…"
                          bind:value={studyFilterText}
                          class="study-filter-input"
                          on:click|stopPropagation
                        />
                      </div>
                      <div class="study-filter-options">
                        <button 
                          type="button"
                          class="study-filter-option" 
                          on:click|stopPropagation={() => toggleAllStudies()}
                          on:keydown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleAllStudies();
                            }
                          }}
                        >
                          <input 
                            type="checkbox" 
                            checked={allStudiesSelected}
                            readonly
                            tabindex="-1"
                            bind:this={selectAllCheckbox}
                            on:click|stopPropagation
                          />
                          <span>Select All</span>
                        </button>
                        {#each filteredStudies as entry (entry.id)}
                          <button 
                            type="button"
                            class="study-filter-option" 
                            on:click={() => toggleStudy(entry.id)}
                            on:keydown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleStudy(entry.id);
                              }
                            }}
                          >
                            <input 
                              type="checkbox" 
                              checked={selectedStudies.has(entry.id)}
                              readonly
                              tabindex="-1"
                            />
                            <span>{entry.name}</span>
                          </button>
                        {/each}
                      </div>
                    </div>
                  {/if}
                </div>
              </div>
              
              <!-- Quick Filter -->
              <div class="tool-group project-extract-toolbar-group--grow quick-filter-wrapper" style="position: relative;">
                <Input id="quick-filter" type="text" placeholder="Quick filter…" bind:value={quickFilterText} class="quick-filter" />
                {#if quickFilterText}
                  <button type="button" class="clear-button clear-quick-filter" on:click={() => { quickFilterText = '';
                      if (gridApi) {
                        gridApi.setGridOption('quickFilterText', '');
                      }
                    }}
                    aria-label="Clear quick filter">
                    <FontAwesomeIcon icon={faCircleXmark} class="w-4 h-4 text-gray-500 hover:text-gray-700" />
                  </button>
                {/if}
              </div>
              
              <!-- Indicator Toggle -->
              <div class="tool-buttons ml-4" style="display: flex; align-items: center; gap: 0.5rem;">
                <span class="tool-toggle-label">Indicator</span>
                <Toggle class="tool-toggle" color="green" bind:checked={showIndicators} size="small" on:change={() => {gridApi?.refreshCells({ force: true });
                  }} />
              </div>
              
              <!-- Export to Excel Button -->
              <div class="tool-buttons" style="display: flex; align-items: center; gap: 0.5rem;">
                <Button size="xs" class="tool-button with-text" color="light" on:click={exportAllTabsToExcel} title="Export">
                  <FontAwesomeIcon icon={faFileExcel} class="w-3 h-3 mr-1" />Export
                </Button>
              </div>
            </div>
          </div>
          <div class="project-extract-tab-panel">
            <div class="project-extract-grid-scroll">
              <div 
                bind:this={gridDiv}
                class="ag-theme-quartz ag-project-extract-grid"
                class:ag-reference-grid={section.type === 'reference'}
                class:ag-topic-grid={section.type === 'topic'}
                data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
              ></div>
            </div>
          </div>
        </TabItem>
      {/each}
    </Tabs>
  </div>
  
  <!-- Hidden grid for export (not visible to user) -->
  <div 
    bind:this={hiddenGridDiv}
    class="ag-theme-quartz"
    style="position: fixed; left: -10000px; top: 0; width: 800px; height: 600px; overflow: hidden; visibility: hidden;"
    data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
  ></div>
  
  <!-- Export Progress Modal -->
  {#if exportModalOpen}
    <div class="export-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="export-modal-title">
      <div class="export-modal-content">
        <div class="text-center p-6">
          <Spinner type="dots" color="green" size="8" class="text-center mb-4" />
          <p id="export-modal-title" class="export-modal-title mb-2">Exporting to Excel</p>
          <p class="text-sm text-gray-600 dark:text-gray-400">{exportProgress || 'Preparing export...'}</p>
        </div>
      </div>
    </div>
  {/if}
</div>

